import { sendPackageReceivedEmail, sendShipmentUpdateEmail, sendConsolidationReadyEmail } from './lib/smtp';
import React, { useState, useEffect, Suspense, lazy } from 'react';
import { LoadingFallback, SkeletonCard } from './components/LoadingFallback';
import { CustomChargePaymentModal } from './components/CustomChargePaymentModal';
import { 
  UserProfile, 
  PackageItem, 
  ShipmentOrder, 
  PackageStatus, 
  ShipmentStatus,
  AppNotification,
  SupportTicket,
  PaymentDetails,
  PersonalShopperOrder,
  PersonalShopperStatus,
  BlogPost,
  HomePopupConfig,
  YahooAuctionBid,
  YahooAuctionStatus
} from './types';
import { 
  INITIAL_DEMO_USERS,
  MASTER_ADMIN_USER,
  ensureAdminUserExists,
  INITIAL_DEMO_PACKAGES, 
  INITIAL_DEMO_SHIPMENTS,
  INITIAL_DEMO_NOTIFICATIONS,
  INITIAL_DEMO_TICKETS,
  INITIAL_DEMO_PERSONAL_SHOPPER,
  INITIAL_DEMO_YAHOO_AUCTION_BIDS,
  INITIAL_DEMO_BLOG_POSTS,
  seedInitialDataIfNeeded,
  isFirestoreQuotaExhausted,
  isQuotaExhaustedError,
  markFirestoreQuotaExhausted,
  db 
} from './lib/firebase';
import { collection, onSnapshot, doc, setDoc, updateDoc, addDoc, deleteDoc } from 'firebase/firestore';

import { Logo } from './components/Logo';
import { Navbar } from './components/Navbar';
import { JapaneseAddressCard } from './components/JapaneseAddressCard';
import { isPackageFreeShipping, getEffectiveCurrencyRates, saveCurrencyRatesConfig } from './lib/shippingAndPayment';
import { AdminSidebar } from './components/AdminSidebar';
import { ProFreeBoxesCounterBar } from './components/ProFreeBoxesCounterBar';
import { MonthlyPerformanceCard } from './components/MonthlyPerformanceCard';
import { sendWhatsAppNotification } from './lib/whatsapp';
import {
  shouldSaveToFirestore,
  shouldSaveToOnamae,
  shouldSaveToLocal,
  FullDatabaseBackupPayload,
  DatabaseTarget
} from './lib/databaseSwitch';
import {
  savePackageToOnamae,
  deletePackageFromOnamae,
  saveShipmentToOnamae,
  saveUserToOnamae
} from './lib/onamaeDb';
import type { InfoTopicId } from './components/InfoMenuModal';
import { LandingPage } from './components/LandingPage';
import { lazyWithRetry } from './lib/lazyWithRetry';

// Lazy Loaded Components for Code-Splitting & PageSpeed Optimization
const PackageCard = lazyWithRetry(() => import('./components/PackageCard'));
const NewTrackingModal = lazyWithRetry(() => import('./components/NewTrackingModal'));
const CheckoutConsolidationModal = lazyWithRetry(() => import('./components/CheckoutConsolidationModal'));
const AdminPackageEntryModal = lazyWithRetry(() => import('./components/AdminPackageEntryModal'));
const AdminPackageExcelImportModal = lazyWithRetry(() => import('./components/AdminPackageExcelImportModal'));
const AdminShipmentManager = lazyWithRetry(() => import('./components/AdminShipmentManager'));
const AdminExtraServicesModal = lazyWithRetry(() => import('./components/AdminExtraServicesModal'));
const SimulatedPaymentModal = lazyWithRetry(() => import('./components/SimulatedPaymentModal'));
const PersonalShopperPaymentModal = lazyWithRetry(() => import('./components/PersonalShopperPaymentModal'));
const AuthModal = lazyWithRetry(() => import('./components/AuthModal'));
const ShippingCalculator = lazyWithRetry(() => import('./components/ShippingCalculator'));
const SupportCenter = lazyWithRetry(() => import('./components/SupportCenter'));
const PersonalShopperSection = lazyWithRetry(() => import('./components/PersonalShopperSection'));
const NossaLojaView = lazyWithRetry(() => import('./components/NossaLojaView'));
const BlogView = lazyWithRetry(() => import('./components/BlogView'));
const InfoCenterView = lazyWithRetry(() => import('./components/InfoMenuModal'));
const LogoUploadModal = lazyWithRetry(() => import('./components/LogoUploadModal'));
const ClientImportModal = lazyWithRetry(() => import('./components/ClientImportModal'));
const UserProfileModal = lazyWithRetry(() => import('./components/UserProfileModal'));
const GoogleDriveModal = lazyWithRetry(() => import('./components/GoogleDriveModal'));
const WhatsAppConfigModal = lazyWithRetry(() => import('./components/WhatsAppConfigModal'));
const PaymentMethodsModal = lazyWithRetry(() => import('./components/PaymentMethodsModal'));
const StorageFeePaymentModal = lazyWithRetry(() => import('./components/StorageFeePaymentModal'));
const AdminClientManagement = lazyWithRetry(() => import('./components/AdminClientManagement'));
const AdminFinancialAnalytics = lazyWithRetry(() => import('./components/AdminFinancialAnalytics'));
const SubscriptionPlansView = lazyWithRetry(() => import('./components/SubscriptionPlansView'));
const HomePopupConfigModal = lazyWithRetry(() => import('./components/HomePopupConfigModal'));
const HomePopupDisplayModal = lazyWithRetry(() => import('./components/HomePopupDisplayModal'));
const AdminEmailMarketingModal = lazyWithRetry(() => import('./components/AdminEmailMarketingModal'));
const AdminSmtpConfigModal = lazyWithRetry(() => import('./components/AdminSmtpConfigModal'));
const GoogleSearchConsoleModal = lazyWithRetry(() => import('./components/GoogleSearchConsoleModal'));
const AdminEmailHistory = lazyWithRetry(() => import('./components/AdminEmailHistory'));
const ShipmentTrackingView = lazyWithRetry(() => import('./components/ShipmentTrackingView'));
const ClientShipmentCard = lazyWithRetry(() => import('./components/ClientShipmentCard'));
const AiSupportChat = lazyWithRetry(() => import('./components/AiSupportChat'));
const InfinitePaySuccessModal = lazyWithRetry(() => import('./components/InfinitePaySuccessModal'));
const AdminCouponsModal = lazyWithRetry(() => import('./components/AdminCouponsModal'));
const AdminExcelProductImportModal = lazyWithRetry(() => import('./components/AdminExcelProductImportModal'));
const AdminOnamaeDbModal = lazyWithRetry(() => import('./components/AdminOnamaeDbModal'));
const AdminExchangeRateModal = lazyWithRetry(() => import('./components/AdminExchangeRateModal'));

import { 
  Box, 
  Truck, 
  CheckCircle2, 
  PackagePlus, 
  Search, 
  Filter, 
  Sparkles, 
  Plane, 
  Paperclip, 
  ArrowRight, 
  ShieldCheck, 
  HelpCircle,
  Bot,
  Plus,
  RefreshCw,
  ShoppingBag,
  Store,
  Calculator,
  Headphones,
  FileSpreadsheet,
  DollarSign
} from 'lucide-react';

export default function App() {
  // Current logged in user (defaults to Master Admin Profile)
  const [currentUser, setCurrentUser] = useState<UserProfile>(MASTER_ADMIN_USER);
  const [allUsers, setAllUsers] = useState<UserProfile[]>(() => {
    try {
      const saved = localStorage.getItem('dojapao_all_users_v2');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const sanitized = parsed.map((u: UserProfile) => {
            if (u.uid === MASTER_ADMIN_USER.uid || u.email?.toLowerCase() === MASTER_ADMIN_USER.email.toLowerCase() || u.role === 'admin') {
              const validPass = (u.password && u.password !== 'teste12345' && u.password !== 'admin123') ? u.password : MASTER_ADMIN_USER.password;
              return { ...u, role: 'admin', suiteNumber: 'DJ-ADMIN', password: validPass };
            }
            return u;
          });
          const hasAdmin = sanitized.some((u: UserProfile) => u.uid === MASTER_ADMIN_USER.uid || u.email?.toLowerCase() === MASTER_ADMIN_USER.email.toLowerCase());
          if (!hasAdmin) {
            return [MASTER_ADMIN_USER, ...sanitized];
          }
          return sanitized;
        }
      }
    } catch (e) {
      console.warn('Could not read users from localStorage:', e);
    }
    return [MASTER_ADMIN_USER];
  });

  // Sync allUsers changes to localStorage for local resilience
  useEffect(() => {
    try {
      localStorage.setItem('dojapao_all_users_v2', JSON.stringify(allUsers));
    } catch (e) {
      console.warn('Could not save users to localStorage:', e);
    }
  }, [allUsers]);

  // Safety hook: Ensure any login with admin email is always forced to role 'admin'
  useEffect(() => {
    if (currentUser.email?.toLowerCase() === MASTER_ADMIN_USER.email.toLowerCase()) {
      if (currentUser.role !== 'admin' || currentUser.suiteNumber !== 'DJ-ADMIN' || currentUser.uid !== MASTER_ADMIN_USER.uid) {
        setCurrentUser(prev => ({
          ...prev,
          ...MASTER_ADMIN_USER,
          role: 'admin',
          suiteNumber: 'DJ-ADMIN',
          uid: MASTER_ADMIN_USER.uid
        }));
      }
    }
  }, [currentUser]);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>('landing');
  const [sharedProductId, setSharedProductId] = useState<string | null>(null);
  const [sharedRaffleId, setSharedRaffleId] = useState<string | null>(null);
  const [sharedAuctionId, setSharedAuctionId] = useState<string | null>(null);
  const [sharedPostId, setSharedPostId] = useState<string | null>(null);
  const [selectedInfoTopic, setSelectedInfoTopic] = useState<InfoTopicId>('salvar-app-celular');

  const [infinitePaySuccessData, setInfinitePaySuccessData] = useState<{
    isOpen: boolean;
    orderNsu?: string;
    transactionNsu?: string;
    captureMethod?: string;
    receiptUrl?: string;
    slug?: string;
  }>({ isOpen: false });

  // Handle InfinitePay return parameters from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const receiptUrl = urlParams.get('receipt_url');
    const orderNsu = urlParams.get('order_nsu');
    const captureMethod = urlParams.get('capture_method');
    const transactionNsu = urlParams.get('transaction_nsu');
    const slug = urlParams.get('slug');
    const payment = urlParams.get('payment');

    if (receiptUrl || orderNsu || transactionNsu || payment === 'success') {
      setInfinitePaySuccessData({
        isOpen: true,
        orderNsu: orderNsu || undefined,
        transactionNsu: transactionNsu || undefined,
        captureMethod: captureMethod || undefined,
        receiptUrl: receiptUrl || undefined,
        slug: slug || undefined,
      });

      // Clean URL query parameters
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const produtoId = urlParams.get('produto');
    const rifaId = urlParams.get('rifa');
    const leilaoId = urlParams.get('leilao');
    const postId = urlParams.get('post');
    let hasParams = false;

    const path = window.location.pathname;
    
    if (path.startsWith('/blog/') && path.length > 6) {
      const slug = path.substring(6); // remove '/blog/'
      if (slug) {
        setSharedPostId(slug);
        setActiveTab('blog');
        hasParams = true;
      }
    } else if (path === '/blog' || path === '/blog/') {
      setActiveTab('blog');
      hasParams = true;
    } else if (produtoId) {
      setSharedProductId(produtoId);
      setActiveTab('nossa-loja');
      hasParams = true;
    } else if (rifaId) {
      setSharedRaffleId(rifaId);
      setActiveTab('nossa-loja');
      hasParams = true;
    } else if (leilaoId) {
      setSharedAuctionId(leilaoId);
      setActiveTab('nossa-loja');
      hasParams = true;
    } else if (postId) {
      setSharedPostId(postId);
      setActiveTab('blog');
      hasParams = true;
    }

    if (hasParams) {
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }, []);

  useEffect(() => {
    const handleOpenInfoTopic = (e: Event) => {
      const customEvent = e as CustomEvent<InfoTopicId>;
      if (customEvent.detail) {
        setSelectedInfoTopic(customEvent.detail);
        setActiveTab('info');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };
    window.addEventListener('openInfoTopic', handleOpenInfoTopic);
    return () => {
      window.removeEventListener('openInfoTopic', handleOpenInfoTopic);
    };
  }, []);

  // Main collections state
      const [paymentCharge, setPaymentCharge] = useState<any | null>(null);
const [customCharges, setCustomCharges] = useState<any[]>([]);
const [packages, setPackages] = useState<PackageItem[]>(INITIAL_DEMO_PACKAGES);
  const [shipments, setShipments] = useState<ShipmentOrder[]>(INITIAL_DEMO_SHIPMENTS);
  const [notifications, setNotifications] = useState<AppNotification[]>(INITIAL_DEMO_NOTIFICATIONS);
  const [tickets, setTickets] = useState<SupportTicket[]>(INITIAL_DEMO_TICKETS);
  const [personalShopperOrders, setPersonalShopperOrders] = useState<PersonalShopperOrder[]>(INITIAL_DEMO_PERSONAL_SHOPPER);
  const [yahooBids, setYahooBids] = useState<YahooAuctionBid[]>(INITIAL_DEMO_YAHOO_AUCTION_BIDS);
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>(INITIAL_DEMO_BLOG_POSTS);

  // Selection & Filters
  const [selectedPackageIds, setSelectedPackageIds] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('A caminho');
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Modals state
  const [isNewTrackingOpen, setIsNewTrackingOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [isAdminEntryOpen, setIsAdminEntryOpen] = useState(false);
  const [isAdminExcelImportOpen, setIsAdminExcelImportOpen] = useState(false);
  const [isClientImportOpen, setIsClientImportOpen] = useState(false);
  const [isAddStoreProductModalOpen, setIsAddStoreProductModalOpen] = useState(false);
  const [isUserProfileOpen, setIsUserProfileOpen] = useState(false);
  const [isGoogleDriveOpen, setIsGoogleDriveOpen] = useState(false);
  const [isWhatsAppConfigOpen, setIsWhatsAppConfigOpen] = useState(false);
  const [isPaymentMethodsModalOpen, setIsPaymentMethodsModalOpen] = useState(false);
  const [isHomePopupConfigModalOpen, setIsHomePopupConfigModalOpen] = useState(false);
  const [isUserHomePopupVisible, setIsUserHomePopupVisible] = useState(false);
  const [isEmailMarketingOpen, setIsEmailMarketingOpen] = useState(false);
  const [isSmtpConfigOpen, setIsSmtpConfigOpen] = useState(false);
  const [isCouponsModalOpen, setIsCouponsModalOpen] = useState(false);
  const [isExcelProductImportModalOpen, setIsExcelProductImportModalOpen] = useState(false);
  const [isExchangeRateModalOpen, setIsExchangeRateModalOpen] = useState(false);
  const [footerJpyRate, setFooterJpyRate] = useState<number>(() => getEffectiveCurrencyRates().jpyToBrl);

  useEffect(() => {
    const handleRateChange = () => {
      setFooterJpyRate(getEffectiveCurrencyRates().jpyToBrl);
    };
    window.addEventListener('currencyRatesUpdated', handleRateChange);
    return () => window.removeEventListener('currencyRatesUpdated', handleRateChange);
  }, []);

  const [homePopupConfig, setHomePopupConfig] = useState<HomePopupConfig>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('home_popup_config');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          return {
            ...parsed,
            enabled: Boolean(parsed.enabled)
          };
        } catch (e) {
          // ignore
        }
      }
    }
    return {
      enabled: false,
      imageUrl: '',
      title: '🎌 Oportunidade Especial do Mês!',
      description: 'Aproveite 60 dias de armazenamento gratuito no nosso depósito em Saitama e consolide todas as suas compras numa caixa só!',
      buttonText: 'Conferir Nossa Loja',
      targetTab: 'loja'
    };
  });

  const handleSaveHomePopupConfig = async (newConfig: HomePopupConfig) => {
    setHomePopupConfig(newConfig);
    if (typeof window !== 'undefined') {
      localStorage.setItem('home_popup_config', JSON.stringify(newConfig));
    }
    try {
      await setDoc(doc(db, 'settings', 'home_popup'), newConfig);
    } catch (err) {
      console.warn('Saved popup config locally:', err);
    }
  };
  const [editingPackage, setEditingPackage] = useState<PackageItem | null>(null);
  const [paymentShipment, setPaymentShipment] = useState<ShipmentOrder | null>(null);
  const [paymentPsOrder, setPaymentPsOrder] = useState<PersonalShopperOrder | null>(null);
  const [storageFeeModalPkg, setStorageFeeModalPkg] = useState<PackageItem | null>(null);
  const [storageFeeOverdueMonths, setStorageFeeOverdueMonths] = useState<number>(1);
  const [storageFeeTotalUsd, setStorageFeeTotalUsd] = useState<number>(30);
  const [isStorageFeeModalOpen, setIsStorageFeeModalOpen] = useState(false);

  const handleOpenStorageFeeModal = (pkg: PackageItem, overdueMonths: number, totalFeeUsd: number) => {
    setStorageFeeModalPkg(pkg);
    setStorageFeeOverdueMonths(overdueMonths);
    setStorageFeeTotalUsd(totalFeeUsd);
    setIsStorageFeeModalOpen(true);
  };

  const handleStorageFeePaymentComplete = async (pkgId: string, paidMonths: number) => {
    const targetPkg = packages.find((p) => p.id === pkgId);
    if (!targetPkg) return;

    const currentMonths = targetPkg.paidStorageMonths || 0;
    const newMonths = currentMonths + paidMonths;

    const updatedPkg = { ...targetPkg, paidStorageMonths: newMonths };
    setPackages((prev) => prev.map((p) => p.id === pkgId ? updatedPkg : p));

    try {
      await updateDoc(doc(db, 'packages', pkgId), { paidStorageMonths: newMonths });
    } catch (e) {
      console.warn('Updated package storage locally:', e);
    }

    triggerPushNotification(
      targetPkg.userId,
      'Armazenamento Regularizado! ✅',
      `A taxa de armazenamento excedido (${paidMonths} mês(es)) do pacote "${targetPkg.description}" foi paga com sucesso. Seu prazo foi estendido por +${paidMonths * 30} dias!`,
      'package_status'
    );
  };

  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [isExtraServicesModalOpen, setIsExtraServicesModalOpen] = useState(false);
  const [isAiChatOpen, setIsAiChatOpen] = useState(false);
  const [isSearchConsoleOpen, setIsSearchConsoleOpen] = useState(false);
  const [isOnamaeDbModalOpen, setIsOnamaeDbModalOpen] = useState(false);
  const [isLogoModalOpen, setIsLogoModalOpen] = useState(false);
  const [customLogoUrl, setCustomLogoUrl] = useState<string | null>(() => localStorage.getItem('customLogoUrl') || '/imagens/logo.png');
  const [customFaviconUrl, setCustomFaviconUrl] = useState<string | null>(() => localStorage.getItem('customFaviconUrl') || '/imagens/icon.png');
  const [siteTitle, setSiteTitle] = useState<string>(() => localStorage.getItem('siteTitle') || 'Redirecionamento do Japão, Mercari JP & Leilão Yahoo Japan | Do Japão até a Sua Casa');

  // Dynamically update document title and favicon links
  useEffect(() => {
    if (siteTitle) {
      document.title = siteTitle;
    }
  }, [siteTitle]);

  useEffect(() => {
    const faviconHref = customFaviconUrl || '/imagens/icon.png';
    let iconLink = document.querySelector<HTMLLinkElement>("link[rel*='icon']");
    if (!iconLink) {
      iconLink = document.createElement('link');
      iconLink.rel = 'shortcut icon';
      document.getElementsByTagName('head')[0].appendChild(iconLink);
    }
    iconLink.href = faviconHref;

    let appleIconLink = document.querySelector<HTMLLinkElement>("link[rel='apple-touch-icon']");
    if (!appleIconLink) {
      appleIconLink = document.createElement('link');
      appleIconLink.rel = 'apple-touch-icon';
      document.getElementsByTagName('head')[0].appendChild(appleIconLink);
    }
    appleIconLink.href = faviconHref;
  }, [customFaviconUrl]);

  const handleSaveLogo = (url: string | null) => {
    setCustomLogoUrl(url);
    if (url) {
      localStorage.setItem('customLogoUrl', url);
    } else {
      localStorage.removeItem('customLogoUrl');
    }
    window.dispatchEvent(new Event('logoUpdated'));
  };

  const handleUpdateUserPlan = async (newPlan: 'free' | 'pro' | 'business') => {
    const nowIso = new Date().toISOString();
    const startDate = currentUser?.subscriptionStartDate || nowIso;
    setCurrentUser((prev) => ({
      ...prev,
      subscriptionPlan: newPlan,
      subscriptionStartDate: startDate
    }));
    try {
      if (currentUser?.uid) {
        await updateDoc(doc(db, 'users', currentUser.uid), {
          subscriptionPlan: newPlan,
          subscriptionStatus: 'active',
          subscriptionStartDate: startDate
        });
      }
    } catch (err) {
      console.warn('Updated user subscription plan locally:', err);
    }
  };

  // Handle return redirect from Stripe Subscription Checkout
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const subscriptionStatus = urlParams.get('subscription');
      const plan = urlParams.get('plan');

      if (subscriptionStatus === 'success' && (plan === 'pro' || plan === 'business')) {
        handleUpdateUserPlan(plan as 'pro' | 'business');
        // Clean URL query parameters
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, []);

  const handleSaveBranding = async (branding: { logoUrl: string | null; faviconUrl: string | null; siteTitle: string }) => {
    handleSaveLogo(branding.logoUrl);

    setCustomFaviconUrl(branding.faviconUrl);
    if (branding.faviconUrl) {
      localStorage.setItem('customFaviconUrl', branding.faviconUrl);
    } else {
      localStorage.removeItem('customFaviconUrl');
    }

    setSiteTitle(branding.siteTitle);
    if (branding.siteTitle) {
      localStorage.setItem('siteTitle', branding.siteTitle);
    } else {
      localStorage.removeItem('siteTitle');
    }

    try {
      await setDoc(doc(db, 'settings', 'branding'), {
        logoUrl: branding.logoUrl,
        faviconUrl: branding.faviconUrl,
        siteTitle: branding.siteTitle,
        updatedAt: new Date().toISOString()
      });
    } catch (err) {
      console.warn('Saved branding to Firestore:', err);
    }
  };

  // Helper to push Web Browser Notification + App Notification
  const triggerPushNotification = async (userId: string, title: string, message: string, type: AppNotification['type']) => {
    const newNotifId = `notif-${Date.now()}`;
    const newNotif: AppNotification = {
      id: newNotifId,
      userId,
      suiteNumber: currentUser.suiteNumber || 'DJ-1002',
      title,
      message,
      read: false,
      createdAt: new Date().toISOString(),
      type
    };

    setNotifications((prev) => [newNotif, ...prev]);

    try {
      await setDoc(doc(db, 'notifications', newNotifId), newNotif);
    } catch (e) {
      console.warn('Saved notification locally:', e);
    }

    // Native Browser Web Push Alert
    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
      new Notification(`Do Japão: ${title}`, {
        body: message,
        icon: customFaviconUrl || '/imagens/icon.png'
      });
    }

    // Automatic WhatsApp Notification Dispatch via Meta Cloud API
    try {
      const targetUser = allUsers.find((u) => u.uid === userId || u.suiteNumber === userId);
      const recipientPhone = targetUser?.phone || currentUser.phone || '080-4859-1502';
      const targetSuite = targetUser?.suiteNumber || currentUser.suiteNumber;
      
      if (recipientPhone) {
        sendWhatsAppNotification(recipientPhone, title, message, targetSuite).catch((err) => {
          console.warn('WhatsApp Notification dispatch notice:', err);
        });
      }
    } catch (err) {
      console.warn('WhatsApp Notification lookup notice:', err);
    }
  };

  // Initialize data and setup Firestore real-time listeners
  useEffect(() => {
    seedInitialDataIfNeeded();

    // Load server-persisted users from /api/data/users to complement local & cloud storage
    fetch('/api/data/users')
      .then((res) => res.json())
      .then((data) => {
        if (data.success && Array.isArray(data.users) && data.users.length > 0) {
          setAllUsers((prev) => {
            const userMap = new Map<string, UserProfile>();
            userMap.set(MASTER_ADMIN_USER.uid, MASTER_ADMIN_USER);
            data.users.forEach((u: UserProfile) => {
              if (u.uid) userMap.set(u.uid, u);
            });
            prev.forEach((u) => {
              if (u.uid) userMap.set(u.uid, u);
            });
            const merged = Array.from(userMap.values());
            try {
              localStorage.setItem('dojapao_all_users_v2', JSON.stringify(merged));
            } catch (e) {}
            return merged;
          });
        }
      })
      .catch(() => {});

    // Listen to real-time packages collection
    const unsubPkgs = onSnapshot(collection(db, 'packages'), (snapshot) => {
      const loaded: PackageItem[] = [];
      snapshot.forEach((d) => loaded.push({ id: d.id, ...d.data() } as PackageItem));
      setPackages(loaded);
    }, (err) => {
      if (isQuotaExhaustedError(err)) {
        markFirestoreQuotaExhausted(err);
      } else {
        console.warn('Firestore real-time sync notice:', err.message);
      }
    });

    // Listen to real-time shipments collection
    const unsubShipments = onSnapshot(collection(db, 'shipments'), (snapshot) => {
      const loaded: ShipmentOrder[] = [];
      snapshot.forEach((d) => loaded.push({ id: d.id, ...d.data() } as ShipmentOrder));
      setShipments(loaded);
    }, (err) => {
      if (isQuotaExhaustedError(err)) {
        markFirestoreQuotaExhausted(err);
      } else {
        console.warn('Firestore real-time sync notice:', err.message);
      }
    });

    // Listen to real-time notifications collection
    const unsubNotifications = onSnapshot(collection(db, 'notifications'), (snapshot) => {
      const loaded: AppNotification[] = [];
      snapshot.forEach((d) => loaded.push({ id: d.id, ...d.data() } as AppNotification));
      setNotifications(loaded);
    }, (err) => {
      if (isQuotaExhaustedError(err)) {
        markFirestoreQuotaExhausted(err);
      } else {
        console.warn('Firestore real-time sync notice:', err.message);
      }
    });

    // Listen to real-time support tickets collection
    const unsubTickets = onSnapshot(collection(db, 'tickets'), (snapshot) => {
      const loaded: SupportTicket[] = [];
      snapshot.forEach((d) => loaded.push({ id: d.id, ...d.data() } as SupportTicket));
      setTickets(loaded);
    }, (err) => {
      if (isQuotaExhaustedError(err)) {
        markFirestoreQuotaExhausted(err);
      } else {
        console.warn('Firestore real-time sync notice:', err.message);
      }
    });

    // Listen to real-time personal shopper collection
    const unsubPersonalShopper = onSnapshot(collection(db, 'personal_shopper'), (snapshot) => {
      const loaded: PersonalShopperOrder[] = [];
      snapshot.forEach((d) => loaded.push({ id: d.id, ...d.data() } as PersonalShopperOrder));
      setPersonalShopperOrders(loaded);
    }, (err) => {
      if (isQuotaExhaustedError(err)) {
        markFirestoreQuotaExhausted(err);
      } else {
        console.warn('Firestore real-time sync notice:', err.message);
      }
    });

    // Listen to real-time users collection
    const unsubUsers = onSnapshot(collection(db, 'users'), (snapshot) => {
      const loaded: UserProfile[] = [];
      const duplicateAdminUidsToDelete: string[] = [];

      snapshot.forEach((d) => {
        const data = d.data() as UserProfile;
        const uid = d.id;
        const emailLower = data.email?.toLowerCase();
        
        // Identify and clean non-admin documents created with admin email
        if (emailLower === MASTER_ADMIN_USER.email.toLowerCase() && uid !== MASTER_ADMIN_USER.uid) {
          duplicateAdminUidsToDelete.push(uid);
        } else {
          loaded.push({ uid, ...data });
        }
      });

      // Purge duplicate admin docs from Firestore asynchronously only if quota allows
      if (duplicateAdminUidsToDelete.length > 0 && !isFirestoreQuotaExhausted) {
        duplicateAdminUidsToDelete.forEach((dupUid) => {
          deleteDoc(doc(db, 'users', dupUid)).catch((e) => {
            if (isQuotaExhaustedError(e)) markFirestoreQuotaExhausted(e);
          });
        });
      }

      const hasMasterAdmin = loaded.some(u => u.uid === MASTER_ADMIN_USER.uid || u.email?.toLowerCase() === MASTER_ADMIN_USER.email.toLowerCase());
      if (!hasMasterAdmin && !isFirestoreQuotaExhausted) {
        ensureAdminUserExists().catch((e) => {
          if (isQuotaExhaustedError(e)) markFirestoreQuotaExhausted(e);
        });
        loaded.unshift(MASTER_ADMIN_USER);
      }

      setAllUsers((prevUsers) => {
        const userMap = new Map<string, UserProfile>();
        userMap.set(MASTER_ADMIN_USER.uid, MASTER_ADMIN_USER);
        loaded.forEach(u => {
          if (u.uid === MASTER_ADMIN_USER.uid || u.email?.toLowerCase() === MASTER_ADMIN_USER.email.toLowerCase() || u.role === 'admin') {
            const adminPass = (u.password && u.password !== 'teste12345' && u.password !== 'admin123') ? u.password : MASTER_ADMIN_USER.password;
            userMap.set(MASTER_ADMIN_USER.uid, { ...u, ...MASTER_ADMIN_USER, role: 'admin', suiteNumber: 'DJ-ADMIN', password: adminPass });
          } else {
            userMap.set(u.uid, u);
          }
        });
        prevUsers.forEach(localU => {
          if (localU.uid !== MASTER_ADMIN_USER.uid && localU.email?.toLowerCase() !== MASTER_ADMIN_USER.email.toLowerCase()) {
            const existing = userMap.get(localU.uid);
            if (existing) {
              userMap.set(localU.uid, {
                ...existing,
                password: localU.password || existing.password
              });
            } else {
              userMap.set(localU.uid, localU);
            }
          }
        });
        return Array.from(userMap.values());
      });
    }, (err) => {
      if (isQuotaExhaustedError(err)) {
        markFirestoreQuotaExhausted(err);
      } else {
        console.warn('Firestore real-time sync notice:', err.message);
      }
    });

    // Listen to real-time blog_posts collection
        const unsubCustomCharges = onSnapshot(collection(db, 'custom_charges'), (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCustomCharges(data as any[]);
    });

const unsubBlog = onSnapshot(collection(db, 'blog_posts'), (snapshot) => {
      if (!snapshot.empty) {
        const loaded: BlogPost[] = [];
        snapshot.forEach((d) => loaded.push({ id: d.id, ...d.data() } as BlogPost));
        loaded.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        setBlogPosts(loaded);
      }
    }, (err) => {
      if (isQuotaExhaustedError(err)) {
        markFirestoreQuotaExhausted(err);
      } else {
        console.warn('Firestore blog_posts sync notice:', err.message);
      }
    });

    // Listen to real-time home popup settings
    const unsubPopup = onSnapshot(doc(db, 'settings', 'home_popup'), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data() as HomePopupConfig;
        setHomePopupConfig(data);
        if (typeof window !== 'undefined') {
          localStorage.setItem('home_popup_config', JSON.stringify(data));
        }
        if (!data.enabled) {
          setIsUserHomePopupVisible(false);
        }
      } else {
        setHomePopupConfig(prev => ({ ...prev, enabled: false }));
        setIsUserHomePopupVisible(false);
      }
    }, (err) => {
      if (isQuotaExhaustedError(err)) {
        markFirestoreQuotaExhausted(err);
      } else {
        console.warn('Firestore popup settings sync notice:', err.message);
      }
    });

    // Listen to real-time branding settings
    const unsubBranding = onSnapshot(doc(db, 'settings', 'branding'), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.logoUrl !== undefined) {
          setCustomLogoUrl(data.logoUrl);
          if (data.logoUrl) localStorage.setItem('customLogoUrl', data.logoUrl);
          else localStorage.removeItem('customLogoUrl');
        }
        if (data.faviconUrl !== undefined) {
          setCustomFaviconUrl(data.faviconUrl);
          if (data.faviconUrl) localStorage.setItem('customFaviconUrl', data.faviconUrl);
          else localStorage.removeItem('customFaviconUrl');
        }
        if (data.siteTitle !== undefined) {
          setSiteTitle(data.siteTitle);
          if (data.siteTitle) localStorage.setItem('siteTitle', data.siteTitle);
          else localStorage.removeItem('siteTitle');
        }
      }
    }, (err) => {
      if (isQuotaExhaustedError(err)) {
        markFirestoreQuotaExhausted(err);
      } else {
        console.warn('Firestore branding settings sync notice:', err.message);
      }
    });

    // Listen to real-time payment gateway settings
    const unsubPaymentGateways = onSnapshot(doc(db, 'settings', 'payment_gateways'), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (typeof window !== 'undefined') {
          localStorage.setItem('dojapao_payment_gateways_config_v1', JSON.stringify(data));
          window.dispatchEvent(new Event('paymentGatewaysUpdated'));
        }
      }
    }, (err) => {
      if (isQuotaExhaustedError(err)) {
        markFirestoreQuotaExhausted(err);
      } else {
        console.warn('Firestore payment gateway settings sync notice:', err.message);
      }
    });

    // Listen to real-time currency exchange rates settings
    const unsubCurrencyRates = onSnapshot(doc(db, 'system_configs', 'currency_rates'), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data() as any;
        if (typeof window !== 'undefined' && data) {
          saveCurrencyRatesConfig(data);
        }
      }
    }, (err) => {
      if (isQuotaExhaustedError(err)) {
        markFirestoreQuotaExhausted(err);
      } else {
        console.warn('Firestore currency exchange rates settings sync notice:', err.message);
      }
    });

    return () => {
      unsubPkgs();
      unsubShipments();
      unsubNotifications();
      unsubTickets();
      unsubPersonalShopper();
      unsubUsers();
      unsubBlog();
      unsubPopup();
      unsubBranding();
      unsubPaymentGateways();
      unsubCurrencyRates();
      unsubCustomCharges();
    };
  }, []);

  // Show Popup on initial landing load ONLY if explicitly enabled and has image
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const isDismissed = sessionStorage.getItem('home_popup_closed');
      if (!isDismissed && homePopupConfig?.enabled === true && Boolean(homePopupConfig?.imageUrl?.trim())) {
        const timer = setTimeout(() => {
          setIsUserHomePopupVisible(true);
        }, 800); // Gentle 800ms delay for smooth entrance
        return () => clearTimeout(timer);
      } else {
        setIsUserHomePopupVisible(false);
      }
    }
  }, [homePopupConfig?.enabled, homePopupConfig?.imageUrl]);

  const isAdmin = isLoggedIn && currentUser.role === 'admin';

  // Blog Post management handlers
  const handleSaveBlogPost = async (postToSave: BlogPost) => {
    setBlogPosts(prev => {
      const idx = prev.findIndex(p => p.id === postToSave.id);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = postToSave;
        return copy;
      }
      return [postToSave, ...prev];
    });

    try {
      await setDoc(doc(db, 'blog_posts', postToSave.id), postToSave);
    } catch (err) {
      console.warn('Saved blog post locally:', err);
    }
  };

  const handleDeleteBlogPost = async (postId: string) => {
    setBlogPosts(prev => prev.filter(p => p.id !== postId));

    try {
      await deleteDoc(doc(db, 'blog_posts', postId));
    } catch (err) {
      console.warn('Deleted blog post locally:', err);
    }
  };

  // Toggle selection for client consolidation
  const toggleSelectPackage = (pkgId: string) => {
    const targetPkg = packages.find((p) => p.id === pkgId);
    if (!targetPkg || targetPkg.isFileOnly) return;

    setSelectedPackageIds((prev) => {
      // Removing item is always allowed
      if (prev.includes(pkgId)) {
        return prev.filter((id) => id !== pkgId);
      }

      // Check current selection compatibility
      const currentlySelected = packages.filter((p) => prev.includes(p.id));
      if (currentlySelected.length > 0) {
        const currentAreFree = currentlySelected.every(isPackageFreeShipping);
        const targetIsFree = isPackageFreeShipping(targetPkg);

        if (currentAreFree !== targetIsFree) {
          if (targetIsFree) {
            alert(
              '⚠️ Este produto possui FRETE GRÁTIS INTERNACIONAL.\n\nProdutos com Frete Grátis só podem ser consolidados com outros produtos que também possuem Frete Grátis. Por favor, desmarque os produtos de frete pago para consolidar este item.'
            );
          } else {
            alert(
              '⚠️ Os produtos atualmente selecionados possuem FRETE GRÁTIS.\n\nProdutos de Frete Normal não podem ser consolidados junto com produtos de Frete Grátis. Consolide e envie os itens de Frete Grátis em uma caixa separada.'
            );
          }
          return prev;
        }
      }

      return [...prev, pkgId];
    });
  };

  // Add new tracking (Client)
  const handleCreateTracking = async (data: Omit<PackageItem, 'id' | 'createdAt'>) => {
    const newId = `pkg-${Date.now()}`;
    const newPkg: PackageItem = {
      ...data,
      id: newId,
      createdAt: new Date().toISOString()
    };

    setPackages((prev) => [newPkg, ...prev]);
    try {
      await setDoc(doc(db, 'packages', newId), newPkg);
    } catch (e) {
      console.warn('Saved to local state:', e);
    }
  };

  const handleAdminPackageExcelImport = async (importedPackages: Omit<PackageItem, 'id' | 'createdAt'>[]) => {
    const newPackages: PackageItem[] = [];
    
    for (const data of importedPackages) {
      const matchedUser = allUsers.find((u) => u.suiteNumber?.toUpperCase() === data.suiteNumber.toUpperCase());
      const userId = matchedUser?.uid || 'import';

      const newId = `pkg-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      const newPkg: PackageItem = {
        ...data,
        userId,
        id: newId,
        createdAt: new Date().toISOString()
      };
      
      newPackages.push(newPkg);
    }

    setPackages((prev) => [...newPackages, ...prev]);

    for (const newPkg of newPackages) {
      if (shouldSaveToFirestore('suiteProducts')) {
        try {
          const cleanPkg = JSON.parse(JSON.stringify(newPkg));
          await setDoc(doc(db, 'packages', newPkg.id), cleanPkg);
        } catch (e) {
          console.warn('Erro ao salvar no Firestore, mantendo estado local:', e);
        }
      }
      
      if (shouldSaveToOnamae('suiteProducts')) {
        try {
          await savePackageToOnamae(newPkg);
        } catch (e) {
          console.warn('Erro ao salvar no Onamae MySQL:', e);
        }
      }

      if (newPkg.userId !== 'import') {
        triggerPushNotification(
          newPkg.userId,
          'Novo Pacote Cadastrado 📦',
          `Seu produto "${newPkg.description}" da ${newPkg.store} foi registrado no depósito.`,
          'package_status'
        );
      }
    }
  };

  // Admin package entry / edit + Push Alert Notification
  const handleAdminPackageEntry = async (data: Omit<PackageItem, 'id' | 'createdAt'>) => {
    if (editingPackage) {
      const updated: PackageItem = { 
        ...editingPackage, 
        ...data,
        photoUrl: data.photoUrl ?? editingPackage.photoUrl ?? ''
      };
      setPackages((prev) => prev.map((p) => p.id === editingPackage.id ? updated : p));
      
      // 1. Check database routing to prevent duplicate or slow writes
      if (shouldSaveToFirestore('suiteProducts')) {
        try {
          const cleanData = JSON.parse(JSON.stringify(data));
          await updateDoc(doc(db, 'packages', editingPackage.id), cleanData);
        } catch (e) {
          console.warn('Erro ao atualizar no Firestore, mantendo atualização local:', e);
        }
      }

      if (shouldSaveToOnamae('suiteProducts')) {
        try {
          await savePackageToOnamae(updated);
        } catch (e) {
          console.warn('Erro ao salvar no Onamae MySQL:', e);
        }
      }

      // If status changed to 'Recebido', alert client!
      if (editingPackage.status !== data.status && data.status === 'Recebido') {
        triggerPushNotification(
          data.userId,
          'Pacote Recebido no Depósito! 📦',
          `Seu produto "${data.description}" da ${data.store} foi conferido e pesado (${data.weightKg} kg) em Saitama.`,
          'package_status'
        );
        const user = allUsers.find(u => u.uid === data.userId);
        if (user && user.email) {
          sendPackageReceivedEmail(user.email, user.name, user.suiteNumber || '', data.description || 'Pacote', data.trackingIn || 'N/A', data.weightKg.toString() + ' kg');
        }
      }

      setEditingPackage(null);
    } else {
      const newId = `pkg-${Date.now()}`;
      const newPkg: PackageItem = {
        ...data,
        id: newId,
        createdAt: new Date().toISOString()
      };
      setPackages((prev) => [newPkg, ...prev]);

      // 1. Check database routing
      if (shouldSaveToFirestore('suiteProducts')) {
        try {
          const cleanPkg = JSON.parse(JSON.stringify(newPkg));
          await setDoc(doc(db, 'packages', newId), cleanPkg);
        } catch (e) {
          console.warn('Erro ao salvar no Firestore, mantendo estado local:', e);
        }
      }

      if (shouldSaveToOnamae('suiteProducts')) {
        try {
          await savePackageToOnamae(newPkg);
        } catch (e) {
          console.warn('Erro ao salvar no Onamae MySQL:', e);
        }
      }

      // Trigger Push notification
      triggerPushNotification(
        data.userId,
        'Novo Pacote Cadastrado 📦',
        `Um novo pacote (${data.description}) foi registrado na sua Suíte ${data.suiteNumber}.`,
        'package_new'
      );

      setStatusFilter('Recebido');
    }
  };

  const handleAdminPackageDelete = async (pkgId: string) => {
    setPackages((prev) => prev.filter((p) => p.id !== pkgId));
    if (shouldSaveToFirestore('suiteProducts')) {
      try {
        await deleteDoc(doc(db, 'packages', pkgId));
      } catch (e) {
        console.warn('Deleted package locally:', e);
      }
    }
    if (shouldSaveToOnamae('suiteProducts')) {
      try {
        await deletePackageFromOnamae(pkgId);
      } catch (e) {}
    }
  };


  const handleCancelConsolidation = async (shipmentId: string) => {
    if (!window.confirm('Tem certeza que deseja cancelar esta consolidação? Os produtos retornarão para a suíte com o status Recebido.')) return;

    const targetShipment = shipments.find((s) => s.id === shipmentId);
    if (!targetShipment) return;
    
    // Find all package IDs associated with this consolidation shipment
    const associatedPkgIds = packages
      .filter((p) => p.consolidationId === shipmentId || targetShipment?.packageIds?.includes(p.id))
      .map((p) => p.id);

    const allPkgIdsToRestore = Array.from(
      new Set([...(targetShipment?.packageIds || []), ...associatedPkgIds])
    );

    // 1. Remove shipment from React state
    setShipments((prev) => prev.filter((s) => s.id !== shipmentId));

    // 2. Update package states back to 'Recebido'
    if (allPkgIdsToRestore.length > 0) {
      setPackages((prev) => prev.map((p) => {
        if (allPkgIdsToRestore.includes(p.id)) {
          return { ...p, status: 'Recebido' as PackageStatus, consolidationId: undefined };
        }
        return p;
      }));
    }

    // 3. Firestore updates
    try {
      const { doc, deleteDoc, updateDoc, deleteField } = await import('firebase/firestore');
      await deleteDoc(doc(db, 'shipments', shipmentId));
      for (const pId of allPkgIdsToRestore) {
        await updateDoc(doc(db, 'packages', pId), {
          status: 'Recebido',
          consolidationId: deleteField()
        }).catch(e => console.warn('Could not restore package:', e));
      }
    } catch(e) {
      console.warn('Failed to cancel consolidation in firestore:', e);
    }
  };

  const handleAdminShipmentDelete = async (shipmentId: string) => {
    const targetShipment = shipments.find((s) => s.id === shipmentId);
    
    // Find all package IDs associated with this consolidation shipment
    const associatedPkgIds = packages
      .filter((p) => p.consolidationId === shipmentId || targetShipment?.packageIds?.includes(p.id))
      .map((p) => p.id);

    const allPkgIdsToDelete = Array.from(
      new Set([...(targetShipment?.packageIds || []), ...associatedPkgIds])
    );

    // 1. Remove shipment from React state
    setShipments((prev) => prev.filter((s) => s.id !== shipmentId));

    // 2. Remove associated packages/products from React state
    if (allPkgIdsToDelete.length > 0) {
      setPackages((prev) => prev.filter((p) => !allPkgIdsToDelete.includes(p.id)));
    }

    // 3. Delete shipment document and package documents from Firestore
    try {
      await deleteDoc(doc(db, 'shipments', shipmentId));
      for (const pId of allPkgIdsToDelete) {
        await deleteDoc(doc(db, 'packages', pId)).catch((e) => console.warn(`Could not delete package ${pId} from firestore`, e));
      }
    } catch (e) {
      console.warn('Deleted shipment and packages locally:', e);
    }
  };

  // Submit consolidation shipment (Client)
  const handleSubmitConsolidation = async (shipmentData: Omit<ShipmentOrder, 'id' | 'createdAt' | 'updatedAt'>) => {
    const shpId = `shp-${Date.now()}`;
    const newShipment: ShipmentOrder = {
      ...shipmentData,
      id: shpId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    // 1. Immediately update React local state
    setShipments((prev) => [newShipment, ...prev]);

    // Update package statuses to "Consolidado"
    setPackages((prev) => prev.map((p) => {
      if (shipmentData.packageIds.includes(p.id)) {
        return { ...p, status: 'Consolidado' as PackageStatus, consolidationId: shpId };
      }
      return p;
    }));

    setSelectedPackageIds([]);
    setActiveTab('shipments');

    // 2. Open payment modal automatically for the new shipment
    setPaymentShipment(newShipment);

    // 3. Persist to Firestore with safety timeout so network latency never hangs the UI
    try {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Firestore timeout')), 2000)
      );
      await Promise.race([
        (async () => {
          await setDoc(doc(db, 'shipments', shpId), newShipment);
          for (const pId of shipmentData.packageIds) {
            await updateDoc(doc(db, 'packages', pId), { status: 'Consolidado', consolidationId: shpId });
          }
        })(),
        timeoutPromise
      ]);
    } catch (e) {
      console.warn('Saved consolidation locally (Firestore async note):', e);
    }
  };

  // Create Personal Shopper order (Client)
  const handleCreatePersonalShopperOrder = async (orderData: Omit<PersonalShopperOrder, 'id' | 'createdAt' | 'updatedAt'>) => {
    const newId = `ps-${Date.now()}`;
    const newOrder: PersonalShopperOrder = {
      ...orderData,
      id: newId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    setPersonalShopperOrders((prev) => [newOrder, ...prev]);

    try {
      await setDoc(doc(db, 'personal_shopper', newId), newOrder);
    } catch (e) {
      console.warn('Saved PS order locally:', e);
    }

    triggerPushNotification(
      currentUser.uid,
      'Solicitação de Compra Assistida Registrada! 🛍️',
      `Sua solicitação do item "${newOrder.productTitle.substring(0, 30)}..." foi registrada para a Suíte ${newOrder.suiteNumber}.`,
      'system'
    );

    return newOrder;
  };

  // Update Personal Shopper order status (Admin or Client payment)
  const handleUpdatePersonalShopperStatus = async (orderId: string, status: PersonalShopperStatus) => {
    setPersonalShopperOrders((prev) =>
      prev.map((o) => (o.id === orderId ? { ...o, status, updatedAt: new Date().toISOString() } : o))
    );

    try {
      await updateDoc(doc(db, 'personal_shopper', orderId), {
        status,
        updatedAt: new Date().toISOString()
      });
    } catch (e) {
      console.warn('Updated PS order locally:', e);
    }
  };

  // Create Yahoo Auction Bid (Virtual Wallet)
  const handleCreateYahooBid = async (bidData: Omit<YahooAuctionBid, 'id' | 'createdAt' | 'updatedAt'>) => {
    const newId = `yauct-${Date.now()}`;
    const newBid: YahooAuctionBid = {
      ...bidData,
      id: newId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    setYahooBids((prev) => [newBid, ...prev]);

    try {
      await setDoc(doc(db, 'yahoo_auction_bids', newId), newBid);
    } catch (e) {
      console.warn('Saved Yahoo Bid locally:', e);
    }

    triggerPushNotification(
      currentUser.uid,
      'Solicitação de Leilão Yahoo Japan Criada! 🏛️',
      `O lance para ${newBid.auctionTitle} foi registrado. Finalize o pagamento para reter o saldo em carteira.`,
      'system'
    );

    return newBid;
  };

  // Update Yahoo Auction Bid status
  const handleUpdateYahooBidStatus = async (bidId: string, status: YahooAuctionStatus, winningPriceJpy?: number) => {
    setYahooBids((prev) =>
      prev.map((b) => {
        if (b.id !== bidId) return b;
        const isRefund = status === 'ultrapassado' || status === 'cancelado';
        return {
          ...b,
          status,
          finalWinningBidJpy: winningPriceJpy || b.finalWinningBidJpy,
          refundDate: isRefund ? new Date().toISOString() : b.refundDate,
          refundAmountBrl: isRefund ? b.heldAmountBrl : b.refundAmountBrl,
          updatedAt: new Date().toISOString()
        };
      })
    );

    try {
      const updatePayload: Record<string, any> = {
        status,
        updatedAt: new Date().toISOString()
      };
      if (winningPriceJpy) updatePayload.finalWinningBidJpy = winningPriceJpy;
      if (status === 'ultrapassado' || status === 'cancelado') {
        updatePayload.refundDate = new Date().toISOString();
      }
      await updateDoc(doc(db, 'yahoo_auction_bids', bidId), updatePayload);
    } catch (e) {
      console.warn('Updated Yahoo Bid locally:', e);
    }

    if (status === 'ultrapassado') {
      triggerPushNotification(
        currentUser.uid,
        'Estorno Automático Confirmado • Leilão Yahoo Japan! ⚡',
        `Seu lance máximo foi ultrapassado. O saldo retido em carteira foi estornado 100% com sucesso.`,
        'system'
      );
    } else if (status === 'arrematado') {
      triggerPushNotification(
        currentUser.uid,
        'Parabéns! Leilão Arrematado no Yahoo Japan! 🏆',
        `Seu lote foi arrematado. A equipe do Personal Shopper iniciou o processamento do envio ao depósito em Saitama.`,
        'system'
      );
    }
  };

  // Update Shipment Status (Admin) + Push Notification
  const handleUpdateShipmentStatus = async (
    shipmentId: string, 
    newStatus: ShipmentStatus, 
    trackingOut?: string, 
    carrier?: string, 
    labelUrl?: string
  ) => {
    let targetUserId = '';
    const targetShipment = shipments.find((s) => s.id === shipmentId);
    if (targetShipment) {
      targetUserId = targetShipment.userId;
      const packageIdsToUpdate = targetShipment.packageIds || [];
      if (packageIdsToUpdate.length > 0) {
        const nextPackageStatus: PackageStatus = newStatus === 'Enviado' ? 'Enviado' : 'Consolidado';
        
        // Update React local state for packages
        setPackages((prev) => prev.map((p) => {
          if (packageIdsToUpdate.includes(p.id)) {
            return { ...p, status: nextPackageStatus };
          }
          return p;
        }));

        // Update packages in Firestore asynchronously
        (async () => {
          for (const pId of packageIdsToUpdate) {
            try {
              await updateDoc(doc(db, 'packages', pId), { status: nextPackageStatus });
            } catch (e) {
              console.warn(`Failed to update package ${pId} status to ${nextPackageStatus}:`, e);
            }
          }
        })();
      }
    }

    setShipments((prev) => prev.map((s) => {
      if (s.id === shipmentId) {
        return {
          ...s,
          status: newStatus,
          trackingOut: trackingOut || s.trackingOut,
          carrier: carrier || s.carrier,
          labelUrl: labelUrl || s.labelUrl,
          updatedAt: new Date().toISOString()
        };
      }
      return s;
    }));

    try {
      await updateDoc(doc(db, 'shipments', shipmentId), {
        status: newStatus,
        trackingOut: trackingOut || '',
        carrier: carrier || '',
        labelUrl: labelUrl || '',
        updatedAt: new Date().toISOString()
      });
    } catch (e) {
      console.warn('Updated shipment locally:', e);
    }

    // Trigger Push Notification to user
    if (targetUserId) {
      if (newStatus === 'Enviado') {
        triggerPushNotification(
          targetUserId,
          'Consolidação Enviada ao Brasil! ✈️',
          `Seu pedido #${shipmentId} foi despachado via ${carrier || 'Japan Post'}. Rastreio: ${trackingOut}`,
          'shipment_status'
        );
      } else if (newStatus === 'Pronto para Despacho') {
        triggerPushNotification(
          targetUserId,
          'Pagamento Aprovado & Embalado 🚚',
          `Seu pedido #${shipmentId} foi embalado no depósito e está pronto para embarque internacional.`,
          'shipment_status'
        );
      }
    }
  };

  // Payment complete handler (Client)
  const handlePaymentComplete = async (shipmentId: string, paymentDetails?: PaymentDetails) => {
    if (shipmentId.startsWith('yauct-')) {
      if (paymentDetails) {
        setYahooBids((prev) =>
          prev.map((b) => (b.id === shipmentId ? { ...b, paymentDetails, status: 'retido' } : b))
        );
        try {
          await updateDoc(doc(db, 'yahoo_auction_bids', shipmentId), {
            paymentDetails,
            status: 'retido',
            updatedAt: new Date().toISOString()
          });
        } catch (e) {
          console.warn('Saved Yahoo payment details locally:', e);
        }
      } else {
        await handleUpdateYahooBidStatus(shipmentId, 'retido');
      }

      triggerPushNotification(
        currentUser.uid,
        'Pagamento de Leilão Yahoo Japan Aprovado! 🏛️',
        `Seu saldo foi pago e retido na carteira virtual com sucesso. Seu lance no Yahoo Japan já está ativo!`,
        'system'
      );
      setPaymentShipment(null);
      return;
    }

    if (shipmentId.startsWith('ps-') || personalShopperOrders.some((o) => o.id === shipmentId)) {
      if (paymentDetails) {
        setPersonalShopperOrders((prev) =>
          prev.map((o) => (o.id === shipmentId ? { ...o, paymentDetails, status: 'Pago - Em Compra' } : o))
        );
        try {
          await updateDoc(doc(db, 'personal_shopper', shipmentId), {
            paymentDetails,
            status: 'Pago - Em Compra',
            updatedAt: new Date().toISOString()
          });
        } catch (e) {
          console.warn('Saved PS payment details locally:', e);
        }
      } else {
        await handleUpdatePersonalShopperStatus(shipmentId, 'Pago - Em Compra');
      }

      triggerPushNotification(
        currentUser.uid,
        'Pagamento de Compra Assistida Aprovado! 💳',
        `Seu pedido de Personal Shopper #${shipmentId} foi pago e nossa equipe em Saitama já iniciou a compra no Japão.`,
        'system'
      );
      setPaymentPsOrder(null);
      setPaymentShipment(null);
      return;
    }

    if (paymentDetails) {
      setShipments((prev) => prev.map((s) => s.id === shipmentId ? { ...s, paymentDetails } : s));
      try {
        await updateDoc(doc(db, 'shipments', shipmentId), { paymentDetails });
      } catch (e) {
        console.warn('Saved payment details locally:', e);
      }
      await handleUpdateShipmentStatus(shipmentId, 'Pronto para Despacho');
    } else {
      setShipments((prev) => prev.map((s) => s.id === shipmentId ? { ...s, paymentDetails: undefined } : s));
      try {
        await updateDoc(doc(db, 'shipments', shipmentId), { paymentDetails: null });
      } catch (e) {
        console.warn('Cleared payment details locally:', e);
      }
      await handleUpdateShipmentStatus(shipmentId, 'Aguardando Pagamento');
    }
    setPaymentShipment(null);
  };

  // Notification Mark as Read & Delete handlers
  const handleMarkNotificationAsRead = async (notificationId: string) => {
    setNotifications((prev) => prev.map((n) => n.id === notificationId ? { ...n, read: true } : n));
    try {
      await updateDoc(doc(db, 'notifications', notificationId), { read: true });
    } catch (e) {
      console.warn('Marked notification read locally:', e);
    }
  };

  const handleMarkAllNotificationsAsRead = async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    try {
      for (const n of notifications) {
        await updateDoc(doc(db, 'notifications', n.id), { read: true });
      }
    } catch (e) {
      console.warn('Marked all read locally:', e);
    }
  };

  const handleDeleteNotification = async (notificationId: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    try {
      await deleteDoc(doc(db, 'notifications', notificationId));
    } catch (e) {
      console.warn('Deleted notification locally:', e);
    }
  };

  const handleDeleteAllNotifications = async () => {
    const userNotifications = notifications.filter(n => {
      if (isAdmin) return true;
      const nSuite = n.suiteNumber?.trim().toUpperCase();
      const userSuite = currentUser.suiteNumber?.trim().toUpperCase();
      const isMySuite = Boolean(nSuite && userSuite && nSuite === userSuite);
      const isMyUid = Boolean(n.userId && n.userId === currentUser.uid);
      return isMySuite || isMyUid;
    });

    const idsToDelete = new Set(userNotifications.map(n => n.id));
    setNotifications((prev) => prev.filter((n) => !idsToDelete.has(n.id)));

    try {
      for (const n of userNotifications) {
        await deleteDoc(doc(db, 'notifications', n.id));
      }
    } catch (e) {
      console.warn('Deleted all notifications locally:', e);
    }
  };

  // Support Tickets Handlers
  const handleCreateSupportTicket = async (ticketData: Omit<SupportTicket, 'id' | 'createdAt' | 'updatedAt'>) => {
    const tId = `ticket-${Date.now()}`;
    const newTicket: SupportTicket = {
      ...ticketData,
      id: tId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    setTickets((prev) => [newTicket, ...prev]);

    try {
      await setDoc(doc(db, 'tickets', tId), newTicket);
    } catch (e) {
      console.warn('Saved ticket locally:', e);
    }
  };

  const handleReplySupportTicket = async (ticketId: string, messageText: string) => {
    const newMsg = {
      id: `msg-${Date.now()}`,
      senderUid: currentUser.uid,
      senderName: currentUser.name,
      senderRole: currentUser.role,
      text: messageText,
      createdAt: new Date().toISOString()
    };

    let targetUser = '';
    setTickets((prev) => prev.map((t) => {
      if (t.id === ticketId) {
        targetUser = t.userId;
        const updatedMsgs = [...t.messages, newMsg];
        const newStatus = currentUser.role === 'admin' ? 'Respondido' as const : 'Em Atendimento' as const;
        return {
          ...t,
          status: newStatus,
          messages: updatedMsgs,
          updatedAt: new Date().toISOString()
        };
      }
      return t;
    }));

    try {
      const current = tickets.find((t) => t.id === ticketId);
      if (current) {
        const updatedMsgs = [...current.messages, newMsg];
        const newStatus = currentUser.role === 'admin' ? 'Respondido' : 'Em Atendimento';
        await updateDoc(doc(db, 'tickets', ticketId), {
          status: newStatus,
          messages: updatedMsgs,
          updatedAt: new Date().toISOString()
        });
      }
    } catch (e) {
      console.warn('Saved reply locally:', e);
    }

    // Push alert if staff replied to client
    if (currentUser.role === 'admin' && targetUser) {
      triggerPushNotification(
        targetUser,
        'Atendimento Do Japão Respondeu 💬',
        `A equipe do depósito enviou uma resposta no seu chamado de suporte.`,
        'support_reply'
      );
    }
  };

  const handleUpdateTicketStatus = async (ticketId: string, status: SupportTicket['status']) => {
    setTickets((prev) => prev.map((t) => t.id === ticketId ? { ...t, status, updatedAt: new Date().toISOString() } : t));
    try {
      await updateDoc(doc(db, 'tickets', ticketId), { status, updatedAt: new Date().toISOString() });
    } catch (e) {
      console.warn('Updated ticket status locally:', e);
    }
  };

  // Filter client packages strictly for current user
  const clientPackages = (isAdmin 
    ? packages 
    : packages.filter((p) => {
        if (!p) return false;
        const pSuite = p.suiteNumber?.trim().toUpperCase();
        const userSuite = currentUser.suiteNumber?.trim().toUpperCase();
        const isMySuite = Boolean(pSuite && userSuite && pSuite === userSuite);
        const isMyUid = Boolean(p.userId && p.userId === currentUser.uid);
        return isMySuite || isMyUid;
      })
  ).map((p) => {
    let effectiveStatus = p.status;
    if (p.consolidationId) {
      const associatedShipment = shipments.find((s) => s.id === p.consolidationId);
      if (associatedShipment?.status === 'Enviado') {
        effectiveStatus = 'Enviado';
      }
    }
    return { ...p, status: effectiveStatus };
  });

  const filteredPackages = clientPackages.filter((p) => {
    const matchesStatus = statusFilter === 'ALL' || p.status === statusFilter;
    const term = (searchTerm || '').trim().toLowerCase();
    if (!term) return matchesStatus;

    const descMatch = (p.description || '').toLowerCase().includes(term);
    const storeMatch = (p.store || '').toLowerCase().includes(term);
    const trackingMatch = (p.trackingIn || '').toLowerCase().includes(term);
    const suiteMatch = (p.suiteNumber || '').toLowerCase().includes(term);
    const barcodeMatch = (p.barcode || '').toLowerCase().includes(term);
    const clientNameMatch = (p.clientName || '').toLowerCase().includes(term);
    const clientEmailMatch = (p.clientEmail || '').toLowerCase().includes(term);

    const ownerUser = allUsers.find(
      (u) => (u.suiteNumber && u.suiteNumber.trim().toUpperCase() === p.suiteNumber?.trim().toUpperCase()) || u.uid === p.userId
    );
    const ownerNameMatch = ownerUser?.name ? ownerUser.name.toLowerCase().includes(term) : false;
    const ownerEmailMatch = ownerUser?.email ? ownerUser.email.toLowerCase().includes(term) : false;

    return matchesStatus && (descMatch || storeMatch || trackingMatch || barcodeMatch || suiteMatch || clientNameMatch || clientEmailMatch || ownerNameMatch || ownerEmailMatch);
  });

  // Client shipments strictly for current user
  const clientShipments = isAdmin
    ? shipments
    : shipments.filter((s) => {
        if (!s) return false;
        const sSuite = s.suiteNumber?.trim().toUpperCase();
        const userSuite = currentUser.suiteNumber?.trim().toUpperCase();
        const isMySuite = Boolean(sSuite && userSuite && sSuite === userSuite);
        const isMyUid = Boolean(s.userId && s.userId === currentUser.uid);
        return isMySuite || isMyUid;
      });

  const selectedPackagesList = packages.filter((p) => selectedPackageIds.includes(p.id));
  const selectedTotalWeightKg = selectedPackagesList.reduce((acc, p) => acc + (p.weightKg || 0), 0);

  const receivedPackagesCount = clientPackages.filter((p) => p.status === 'Recebido').length;
  const pendingShipmentsCount = shipments.filter((s) => s.status !== 'Enviado').length;

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-800 flex flex-col selection:bg-sky-500 selection:text-white">
      
      {/* Top Header Navigation */}
      <Navbar
        currentUser={currentUser}
        isLoggedIn={isLoggedIn}
        onLogout={() => {
          setIsLoggedIn(false);
          setActiveTab('landing');
        }}
        activeTab={activeTab}
        setActiveTab={(tab) => {
          if (!isLoggedIn && (tab === 'dashboard' || tab === 'packages' || tab === 'shipments' || tab === 'personal-shopper' || tab === 'support')) {
            setIsAuthOpen(true);
            return;
          }
          setActiveTab(tab);
        }}
        onOpenAuth={() => setIsAuthOpen(true)}
        onOpenNewTracking={() => {
          if (!isLoggedIn) {
            setIsAuthOpen(true);
            return;
          }
          setIsNewTrackingOpen(true);
        }}
        onOpenAdminEntry={() => {
          setEditingPackage(null);
          setIsAdminEntryOpen(true);
        }}
        onOpenLogoModal={() => setIsLogoModalOpen(true)}
        onOpenClientImportModal={() => setIsClientImportOpen(true)}
        onOpenAddStoreProductModal={() => {
          setActiveTab('nossa-loja');
          setIsAddStoreProductModalOpen(true);
        }}
        onOpenUserProfileModal={() => setIsUserProfileOpen(true)}
        onOpenGoogleDriveModal={() => setIsGoogleDriveOpen(true)}
        onOpenWhatsAppModal={() => setIsWhatsAppConfigOpen(true)}
        onOpenPaymentMethodsModal={() => setIsPaymentMethodsModalOpen(true)}
        onOpenHomePopupModal={() => setIsHomePopupConfigModalOpen(true)}
        demoUsers={allUsers}
        onSwitchUser={(u) => {
          const isMasterAdmin = u.email?.toLowerCase() === MASTER_ADMIN_USER.email.toLowerCase() || u.uid === MASTER_ADMIN_USER.uid || u.role === 'admin';
          const finalUser: UserProfile = isMasterAdmin ? { ...u, ...MASTER_ADMIN_USER, role: 'admin', suiteNumber: 'DJ-ADMIN' } : u;
          setCurrentUser(finalUser);
          setIsLoggedIn(true);
          setActiveTab(finalUser.role === 'admin' ? 'admin-packages' : 'dashboard');
        }}
        receivedCount={receivedPackagesCount}
        pendingShipmentsCount={pendingShipmentsCount}
        notifications={notifications.filter(n => {
          if (isAdmin) return true;
          const nSuite = n.suiteNumber?.trim().toUpperCase();
          const userSuite = currentUser.suiteNumber?.trim().toUpperCase();
          const isMySuite = Boolean(nSuite && userSuite && nSuite === userSuite);
          const isMyUid = Boolean(n.userId && n.userId === currentUser.uid);
          return isMySuite || isMyUid;
        })}
        onMarkNotificationAsRead={handleMarkNotificationAsRead}
        onMarkAllNotificationsAsRead={handleMarkAllNotificationsAsRead}
        onDeleteNotification={handleDeleteNotification}
        onDeleteAllNotifications={handleDeleteAllNotifications}
      />

      {/* Admin Expandable Side Drawer / Sidebar */}
      <AdminSidebar
        isAdmin={isAdmin}
        isLoggedIn={isLoggedIn}
        onOpenAdminEntry={() => {
          setEditingPackage(null);
          setIsAdminEntryOpen(true);
        }}
        onOpenPackageExcelImport={() => setIsAdminExcelImportOpen(true)}
        onOpenClientImportModal={() => setIsClientImportOpen(true)}
        onOpenAddStoreProductModal={() => {
          setActiveTab('nossa-loja');
          setIsAddStoreProductModalOpen(true);
        }}
        onOpenCouponsModal={() => setIsCouponsModalOpen(true)}
        onOpenExcelProductImportModal={() => {
          setActiveTab('nossa-loja');
          setIsExcelProductImportModalOpen(true);
        }}
        onOpenWhatsAppModal={() => setIsWhatsAppConfigOpen(true)}
        onOpenGoogleDriveModal={() => setIsGoogleDriveOpen(true)}
        onOpenLogoModal={() => setIsLogoModalOpen(true)}
        onOpenPaymentMethodsModal={() => setIsPaymentMethodsModalOpen(true)}
        onOpenExchangeRateModal={() => setIsExchangeRateModalOpen(true)}
        onOpenHomePopupModal={() => setIsHomePopupConfigModalOpen(true)}
        onOpenEmailMarketingModal={() => setIsEmailMarketingOpen(true)}
        onOpenSmtpConfigModal={() => setIsSmtpConfigOpen(true)}
        onOpenSearchConsoleModal={() => setIsSearchConsoleOpen(true)}
        onOpenOnamaeDbModal={() => setIsOnamaeDbModalOpen(true)}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        pendingShipmentsCount={pendingShipmentsCount}
      />

      {/* Landing Page View */}
      <Suspense fallback={<LoadingFallback message="Carregando visualização..." />}>
        {activeTab === 'landing' ? (
          <div className="flex-1 animate-fade-in">
            <LandingPage
              onEnterSuite={() => {
                if (isLoggedIn) {
                  setActiveTab(currentUser.role === 'admin' ? 'admin-packages' : 'dashboard');
                } else {
                  setIsAuthOpen(true);
                }
              }}
              onOpenAuth={() => setIsAuthOpen(true)}
              onOpenStoresGuide={() => {
                window.dispatchEvent(new CustomEvent('openInfoTopic', { detail: 'sites-compras-japao' }));
              }}
              suiteNumber={currentUser.suiteNumber}
              userName={currentUser.name}
              isLoggedIn={isLoggedIn}
            />
          </div>
        ) : activeTab === 'blog' ? (
          <div className="flex-1 animate-fade-in">
            <BlogView
              posts={blogPosts}
              sharedPostId={sharedPostId}
              currentUser={currentUser}
              isLoggedIn={isLoggedIn}
              onSavePost={handleSaveBlogPost}
              onDeletePost={handleDeleteBlogPost}
              onEnterSuite={() => {
                if (isLoggedIn) {
                  setActiveTab(currentUser.role === 'admin' ? 'admin-packages' : 'dashboard');
                } else {
                  setIsAuthOpen(true);
                }
              }}
              onOpenAuth={() => setIsAuthOpen(true)}
            />
          </div>
        ) : activeTab === 'info' ? (
          <div className="flex-1 animate-fade-in">
            <InfoCenterView
              setActiveTab={setActiveTab}
              selectedTopic={selectedInfoTopic}
              onSelectTopic={(topic) => setSelectedInfoTopic(topic)}
            />
          </div>
        ) : (
          /* Main Dashboard Page Layout */
          <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6 bg-[#F1F5F9]">

          {/* ---------------- CALCULATOR TAB ---------------- */}
          {activeTab === 'calculator' && (
            <div className="animate-fade-in">
              <ShippingCalculator packages={clientPackages} isAdmin={isAdmin} />
            </div>
          )}

          {/* ---------------- SUPPORT CENTER TAB ---------------- */}
          {activeTab === 'support' && (
            <div className="animate-fade-in">
              <SupportCenter
                currentUser={currentUser}
                tickets={tickets}
                onCreateTicket={handleCreateSupportTicket}
                onReplyTicket={handleReplySupportTicket}
                onUpdateTicketStatus={handleUpdateTicketStatus}
              />
            </div>
          )}

        {/* ---------------- CLIENT DASHBOARD (BENTO GRID) ---------------- */}
        {!isAdmin && activeTab === 'dashboard' && (
          <div className="space-y-6 animate-fade-in">
            
            {/* PRO Plan Monthly Free Boxes Counter Bar */}
            <ProFreeBoxesCounterBar 
              currentUser={currentUser} 
              shipments={clientShipments} 
              onNavigateToPlans={() => setActiveTab('subscription-plans')}
            />

            {/* Top Bento Row: Address + Suite Summary */}
            <div className="grid grid-cols-12 gap-4 sm:gap-6">
              
              {/* Address Card (7 Columns) */}
              <div className="col-span-12 lg:col-span-7">
                <JapaneseAddressCard
                  suiteNumber={currentUser.suiteNumber}
                  userName={currentUser.name}
                />
              </div>

              {/* Bento Suite Summary Card (5 Columns) in #1D3557 Navy */}
              <div className="col-span-12 lg:col-span-5 bg-[#1D3557] rounded-2xl p-6 shadow-md text-white flex flex-col justify-between border border-slate-800 relative overflow-hidden">
                <div className="flex justify-between items-center relative z-10">
                  <div className="flex items-center gap-2">
                    <Box className="w-4 h-4 text-[#E63946]" />
                    <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider">
                      Resumo da Suíte [{currentUser.suiteNumber}]
                    </h3>
                  </div>
                  <span className="px-2.5 py-0.5 bg-emerald-500 text-white font-black text-[10px] rounded uppercase tracking-widest">
                    SINCRO LIVE
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 my-6 relative z-10">
                  <div className="p-3 bg-white/5 rounded-xl border border-white/10">
                    <div className="text-3xl font-black text-white">
                      {clientPackages.length}
                    </div>
                    <div className="text-[10px] text-slate-300 uppercase font-bold tracking-wider mt-1">
                      Total de Caixas
                    </div>
                  </div>

                  <div className="p-3 bg-white/5 rounded-xl border border-white/10">
                    <div className="text-3xl font-black text-[#E63946]">
                      {clientPackages.reduce((acc, p) => acc + (p.weightKg || 0), 0).toFixed(2)}
                      <span className="text-xs font-bold text-slate-300 ml-1">kg</span>
                    </div>
                    <div className="text-[10px] text-slate-300 uppercase font-bold tracking-wider mt-1">
                      Peso Acumulado
                    </div>
                  </div>
                </div>

                <div className="space-y-1.5 relative z-10">
                  <div className="flex justify-between text-[11px] font-bold text-slate-300">
                    <span>Ocupação da Suíte</span>
                    <span className="text-[#E63946]">{clientPackages.filter(p => p.status === 'Recebido').length} Prontos p/ Envio</span>
                  </div>
                  <div className="h-2 bg-slate-800 rounded-full overflow-hidden p-0.5 border border-slate-700">
                    <div 
                      className="h-full bg-[#E63946] rounded-full transition-all duration-500" 
                      style={{ width: `${Math.min(100, (clientPackages.length / 10) * 100)}%` }}
                    />
                  </div>
                </div>
              </div>

            </div>

            {/* Quick Action & Stats Bento Row (5 Grid Cards) */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
              <div 
                onClick={() => setActiveTab('packages')}
                className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200/80 shadow-2xs hover:shadow-md transition-all cursor-pointer group flex flex-col justify-between"
              >
                <div className="flex justify-between items-start mb-2">
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Recebidos no Depósito</span>
                  <div className="p-2 bg-emerald-50 text-emerald-600 rounded-xl group-hover:scale-110 transition-transform">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-black text-[#1D3557]">
                    {clientPackages.filter((p) => p.status === 'Recebido').length}
                  </div>
                  <span className="text-[11px] text-emerald-600 font-bold mt-1 block">Prontos para consolidar</span>
                </div>
              </div>

              <div 
                onClick={() => setActiveTab('packages')}
                className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200/80 shadow-2xs hover:shadow-md transition-all cursor-pointer group flex flex-col justify-between"
              >
                <div className="flex justify-between items-start mb-2">
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">A Caminho (Japão)</span>
                  <div className="p-2 bg-amber-50 text-amber-600 rounded-xl group-hover:scale-110 transition-transform">
                    <Truck className="w-5 h-5" />
                  </div>
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-black text-[#1D3557]">
                    {clientPackages.filter((p) => p.status === 'A caminho').length}
                  </div>
                  <span className="text-[11px] text-amber-600 font-bold mt-1 block">Em trânsito doméstico</span>
                </div>
              </div>

              <div 
                onClick={() => setActiveTab('shipments')}
                className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200/80 shadow-2xs hover:shadow-md transition-all cursor-pointer group flex flex-col justify-between"
              >
                <div className="flex justify-between items-start mb-2">
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Consolidações</span>
                  <div className="p-2 bg-blue-50 text-blue-600 rounded-xl group-hover:scale-110 transition-transform">
                    <Box className="w-5 h-5" />
                  </div>
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-black text-[#1D3557]">
                    {clientShipments.length}
                  </div>
                  <span className="text-[11px] text-blue-600 font-bold mt-1 block">Envios solicitados</span>
                </div>
              </div>

              {/* Bento Personal Shopper Indigo Card */}
              <div 
                onClick={() => setActiveTab('personal-shopper')}
                className="bg-gradient-to-br from-indigo-900 to-[#1D3557] text-white p-4 sm:p-5 rounded-2xl shadow-md hover:shadow-lg transition-all cursor-pointer flex flex-col justify-between group border border-indigo-700"
              >
                <div className="flex justify-between items-start">
                  <span className="text-xs font-extrabold text-indigo-200 uppercase tracking-wider flex items-center gap-1">
                    <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                    <span>Personal Shopper</span>
                  </span>
                  <div className="p-2 bg-white/20 rounded-xl text-white group-hover:scale-110 transition-transform">
                    <ShoppingBag className="w-5 h-5 text-indigo-300" />
                  </div>
                </div>
                <div>
                  <h4 className="font-extrabold text-sm sm:text-base leading-tight text-white">Comprar por Link 🛍️</h4>
                  <p className="text-[11px] text-indigo-200 mt-0.5 font-medium">Scrape do Mercari, Yahoo Auctions...</p>
                </div>
              </div>

              {/* Bento Nossa Loja Red Card */}
              <div 
                onClick={() => setActiveTab('nossa-loja')}
                className="bg-gradient-to-br from-[#E63946] to-red-900 text-white p-4 sm:p-5 rounded-2xl shadow-md hover:shadow-lg transition-all cursor-pointer flex flex-col justify-between group border border-red-600"
              >
                <div className="flex justify-between items-start">
                  <span className="text-xs font-extrabold text-red-100 uppercase tracking-wider flex items-center gap-1">
                    <Sparkles className="w-3.5 h-3.5 text-amber-300" />
                    <span>Nossa Loja</span>
                  </span>
                  <div className="p-2 bg-white/20 rounded-xl text-white group-hover:scale-110 transition-transform">
                    <Store className="w-5 h-5 text-red-100" />
                  </div>
                </div>
                <div>
                  <h4 className="font-extrabold text-sm sm:text-base leading-tight text-white">Loja do Japão 🏪</h4>
                  <p className="text-[11px] text-red-100 mt-0.5 font-medium">Pronta entrega & direto pra Suíte</p>
                </div>
              </div>
            </div>

            {/* Monthly Performance Card */}
            <MonthlyPerformanceCard 
              packages={clientPackages} 
              shipments={clientShipments} 
            />

            {/* Package Gallery Bento Card */}
            <div className="bg-white rounded-2xl p-6 shadow-2xs border border-slate-200/80 space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-base sm:text-lg font-bold text-[#1D3557] uppercase tracking-wider underline decoration-[#E63946] decoration-2 underline-offset-4">
                  Pacotes Recentes na Suíte
                </h3>
                <button
                  onClick={() => setActiveTab('packages')}
                  className="text-xs font-bold text-[#1D3557] hover:text-[#E63946] transition-colors flex items-center gap-1"
                >
                  <span>Ver Todos os Pacotes</span>
                  <ArrowRight className="w-3.5 h-3.5 text-[#E63946]" />
                </button>
              </div>

              {clientPackages.length === 0 ? (
                <div className="bg-slate-50/80 rounded-xl border border-slate-200/60 p-8 text-center text-slate-400 space-y-3">
                  <ShoppingBag className="w-12 h-12 mx-auto stroke-1 text-slate-300" />
                  <p className="font-semibold text-sm text-[#1D3557]">Você ainda não possui nenhum pacote cadastrado na sua Suíte {currentUser.suiteNumber}.</p>
                  <button
                    onClick={() => setIsNewTrackingOpen(true)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#E63946] hover:bg-[#d62839] text-white font-bold text-xs rounded-xl shadow-md"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Cadastrar Primeira Compra</span>
                  </button>
                </div>
              ) : (
                <Suspense fallback={<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"><SkeletonCard /><SkeletonCard /><SkeletonCard /></div>}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {clientPackages.slice(0, 3).map((pkg) => (
                      <PackageCard
                        key={pkg.id}
                        pkg={pkg}
                        isSelected={selectedPackageIds.includes(pkg.id)}
                        onSelectToggle={toggleSelectPackage}
                        onPayStorageFee={handleOpenStorageFeeModal}
                        userPlan={currentUser?.subscriptionPlan || 'free'}
                      />
                    ))}
                  </div>
                </Suspense>
              )}
            </div>

          </div>
        )}

        {/* ---------------- CLIENT PACKAGES LIST VIEW ---------------- */}
        {!isAdmin && activeTab === 'packages' && (
          <div className="space-y-6 animate-fade-in">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-[#1D3557] flex items-center gap-2">
                  <span>Meus Pacotes no Depósito</span>
                  <span className="text-xs px-2.5 py-0.5 bg-blue-50 text-blue-600 rounded-full border border-blue-100 font-extrabold uppercase font-mono">
                    SUÍTE {currentUser.suiteNumber}
                  </span>
                </h2>
                <p className="text-slate-500 text-xs sm:text-sm">
                  Gerencie compras recebidas no Japão e selecione quais caixas deseja consolidar
                </p>
              </div>

              <button
                onClick={() => setIsNewTrackingOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#E63946] hover:bg-[#d62839] text-white font-bold text-xs sm:text-sm rounded-xl shadow-md shadow-red-500/10 transition-all"
              >
                <PackagePlus className="w-4 h-4 text-white" />
                <span>Cadastrar Novo Rastreio</span>
              </button>
            </div>

            
            {/* Custom Charges Section */}
            {customCharges.filter(c => c.userId === currentUser.uid && c.status === 'Pendente').length > 0 && (
              <div className="mb-6 space-y-3">
                <h3 className="font-bold text-amber-600 flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  Cobranças Avulsas Pendentes
                </h3>
                {customCharges
                  .filter(c => c.userId === currentUser.uid && c.status === 'Pendente')
                  .map(charge => (
                    <div key={charge.id} className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 shadow-sm">
                      <div>
                        <h4 className="font-bold text-slate-800">{charge.title}</h4>
                        {charge.description && <p className="text-sm text-slate-600 mt-1">{charge.description}</p>}
                        <p className="text-xs text-amber-700 font-bold mt-2">
                          Data: {new Date(charge.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-2 w-full sm:w-auto">
                        <div className="text-xl font-black text-amber-700 whitespace-nowrap">
                          ¥ {charge.amountJpy.toLocaleString('ja-JP')}
                        </div>
                        <button
                          onClick={() => setPaymentCharge(charge)}
                          className="w-full sm:w-auto px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl transition-all shadow-md active:scale-95 cursor-pointer"
                        >
                          Pagar Agora
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            )}

            {/* Filter & Search Toolbar */}
            <div className="bg-white p-3.5 rounded-2xl border border-slate-200/80 shadow-2xs flex flex-col sm:flex-row items-center justify-between gap-3">
              {/* Status Filter Badges */}
              <div className="flex items-center gap-1.5 overflow-x-auto w-full sm:w-auto pb-1 sm:pb-0">
                {[
                  { label: 'Todos', value: 'ALL' },
                  { label: 'Recebido', value: 'Recebido' },
                  { label: 'A caminho', value: 'A caminho' },
                  { label: 'Consolidado', value: 'Consolidado' },
                  { label: 'Enviado', value: 'Enviado' }
                ].map((f) => (
                  <button
                    key={f.value}
                    onClick={() => setStatusFilter(f.value)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap ${
                      statusFilter === f.value
                        ? 'bg-[#1D3557] text-white shadow-2xs'
                        : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              {/* Search Box */}
              <div className="relative w-full sm:w-64">
                <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar produto, loja, rastreio ou código de barras..."
                  className="w-full pl-9 pr-3 py-1.5 rounded-xl border border-slate-200 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-[#1D3557]/20"
                />
              </div>
            </div>

            {/* Package Cards Grid */}
            {filteredPackages.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-400 space-y-2">
                <Box className="w-12 h-12 mx-auto stroke-1 text-slate-300" />
                <p className="font-semibold text-sm text-[#1D3557]">Nenhum pacote encontrado para o filtro selecionado.</p>
              </div>
            ) : (
              <Suspense fallback={<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"><SkeletonCard /><SkeletonCard /><SkeletonCard /></div>}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredPackages.map((pkg) => (
                    <PackageCard
                      key={pkg.id}
                      pkg={pkg}
                      isSelected={selectedPackageIds.includes(pkg.id)}
                      onSelectToggle={toggleSelectPackage}
                      onPayStorageFee={handleOpenStorageFeeModal}
                      userPlan={currentUser?.subscriptionPlan || 'free'}
                    />
                  ))}
                </div>
              </Suspense>
            )}
          </div>
        )}

        {/* ---------------- CLIENT CONSOLIDATIONS LIST VIEW ---------------- */}
        {!isAdmin && activeTab === 'shipments' && (
          <div className="space-y-6 animate-fade-in">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-slate-800">Minhas Consolidações & Envio Internacional</h2>
                <p className="text-slate-500 text-xs sm:text-sm">
                  Histórico de fretes internacionais e acompanhamento do despacho até o Brasil
                </p>
              </div>
            </div>

            {/* PRO Plan Counter Bar */}
            <ProFreeBoxesCounterBar 
              currentUser={currentUser} 
              shipments={clientShipments} 
              onNavigateToPlans={() => setActiveTab('subscription-plans')}
            />

            {clientShipments.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-400 space-y-3">
                <Truck className="w-12 h-12 mx-auto stroke-1 text-slate-300" />
                <p className="font-semibold text-sm">Você ainda não solicitou nenhuma consolidação.</p>
                <p className="text-xs text-slate-400">Vá até "Meus Pacotes", marque as caixas com status "Recebido" e clique em Consolidar!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {clientShipments.map((shp) => (
                  <ClientShipmentCard 
                    key={shp.id} 
                    shp={shp} 
                    onPayFreight={setPaymentShipment} 
                    onCancelConsolidation={() => handleCancelConsolidation(shp.id)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ---------------- PERSONAL SHOPPER VIEW ---------------- */}
        {activeTab === 'personal-shopper' && (
          <PersonalShopperSection
            currentUser={currentUser}
            orders={personalShopperOrders}
            onCreateOrder={handleCreatePersonalShopperOrder}
            onUpdateStatus={handleUpdatePersonalShopperStatus}
            allUsers={allUsers}
            yahooBids={yahooBids}
            onCreateYahooBid={handleCreateYahooBid}
            onUpdateYahooBidStatus={handleUpdateYahooBidStatus}
            onOpenPaymentModal={(psOrder) => {
              setPaymentPsOrder(psOrder);
            }}
          />
        )}

        {/* ---------------- NOSSA LOJA VIEW ---------------- */}
        {activeTab === 'nossa-loja' && (
          <NossaLojaView
            currentUser={currentUser}
            sharedProductId={sharedProductId}
            sharedRaffleId={sharedRaffleId}
            sharedAuctionId={sharedAuctionId}
            isLoggedIn={isLoggedIn}
            onOpenAuth={() => setIsAuthOpen(true)}
            onAddPackageToSuite={async (newPkgData) => {
              const fullPkg: Omit<PackageItem, 'id' | 'createdAt'> = {
                suiteNumber: newPkgData.suiteNumber || currentUser.suiteNumber,
                userId: newPkgData.userId || currentUser.uid,
                trackingIn: newPkgData.trackingIn || `LOJA-JP-${Math.floor(100000 + Math.random() * 900000)}`,
                store: newPkgData.store || 'Nossa Loja Do Japão',
                description: newPkgData.description || 'Produto da Nossa Loja Do Japão',
                declaredValue: newPkgData.declaredValue || 0,
                weightKg: newPkgData.weightKg || 0.5,
                weightLb: (newPkgData.weightKg || 0.5) * 2.20462,
                status: (newPkgData.status as PackageStatus) || 'Recebido',
                photoUrl: newPkgData.photoUrl || (newPkgData.photoUrls && newPkgData.photoUrls[0]) || '',
                arrivalDate: newPkgData.receivedDate || new Date().toISOString()
              };
              await handleAdminPackageEntry(fullPkg);
            }}
            onNavigateToSuite={() => setActiveTab('packages')}
            isAdmin={isAdmin}
            allUsers={allUsers}
            isAddProductOpenFromParent={isAddStoreProductModalOpen}
            onCloseAddProductFromParent={() => setIsAddStoreProductModalOpen(false)}
            onOpenExcelProductModal={() => setIsExcelProductImportModalOpen(true)}
          />
        )}

        {/* ---------------- SUBSCRIPTION PLANS VIEW ---------------- */}
        {activeTab === 'subscription-plans' && (
          <SubscriptionPlansView
            currentUser={currentUser}
            onUpdateUserPlan={handleUpdateUserPlan}
            onOpenAuth={() => setIsAuthOpen(true)}
            onOpenPaymentMethodsModal={() => setIsPaymentMethodsModalOpen(true)}
          />
        )}

        {/* ---------------- ADMIN PANELS ---------------- */}
        {isAdmin && activeTab === 'admin-packages' && (
          <div className="space-y-6 animate-fade-in">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-slate-800">
                  Estoque Geral do Depósito (Japão)
                </h2>
                <p className="text-slate-500 text-xs sm:text-sm">
                  Recebimento de caixas, pesagem, fotos e controle por suíte de cliente
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsAdminExcelImportOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs sm:text-sm rounded-xl transition-all"
                >
                  <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                  <span className="hidden sm:inline">Importar Pacotes</span>
                </button>
                <button
                  onClick={() => {
                    setEditingPackage(null);
                    setIsAdminEntryOpen(true);
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs sm:text-sm rounded-xl shadow-md shadow-emerald-500/20 transition-all"
                >
                  <Box className="w-4 h-4 text-amber-300" />
                  <span>Dar Entrada em Pacote</span>
                </button>
              </div>
            </div>

            {/* Admin Filter Toolbar */}
            <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-2xs flex flex-col sm:flex-row items-center justify-between gap-3">
              {/* Status Filter Badges */}
              <div className="flex items-center gap-1.5 overflow-x-auto w-full sm:w-auto pb-1 sm:pb-0">
                {[
                  { label: 'Todos', value: 'ALL' },
                  { label: 'A caminho', value: 'A caminho' },
                  { label: 'Recebido', value: 'Recebido' },
                  { label: 'Consolidado', value: 'Consolidado' },
                  { label: 'Enviado', value: 'Enviado' }
                ].map((f) => (
                  <button
                    key={f.value}
                    onClick={() => setStatusFilter(f.value)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                      statusFilter === f.value
                        ? 'bg-emerald-700 text-white shadow-2xs'
                        : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              <div className="relative w-full sm:w-80">
                <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Filtrar por Suíte, cliente, produto ou rastreio..."
                  className="w-full pl-9 pr-3 py-1.5 rounded-xl border border-slate-200 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                />
              </div>

              <div className="text-xs font-bold text-slate-500 shrink-0">
                Exibindo {filteredPackages.length} {filteredPackages.length === 1 ? 'pacote' : 'pacotes'}
              </div>
            </div>

            <Suspense fallback={<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"><SkeletonCard /><SkeletonCard /><SkeletonCard /></div>}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredPackages.map((pkg) => (
                  <PackageCard
                    key={pkg.id}
                    pkg={pkg}
                    isAdmin={true}
                    onAdminEdit={(p) => {
                      setEditingPackage(p);
                      setIsAdminEntryOpen(true);
                    }}
                    onPayStorageFee={handleOpenStorageFeeModal}
                    onAdminDelete={handleAdminPackageDelete}
                    userPlan={allUsers.find(u => u.uid === pkg.userId)?.subscriptionPlan || 'free'}
                  />
                ))}
              </div>
            </Suspense>
          </div>
        )}

        {isAdmin && activeTab === 'admin-shipments' && (
          <Suspense fallback={<LoadingFallback message="Carregando Gestor de Envíos..." />}>
            <AdminShipmentManager
              shipments={shipments}
              allPackages={packages}
              onUpdateShipmentStatus={handleUpdateShipmentStatus}
              onUpdatePaymentDetails={handlePaymentComplete}
              customCharges={customCharges}
              onDeleteShipment={handleAdminShipmentDelete}
              onDeletePackage={handleAdminPackageDelete}
              onOpenExtraServices={() => setIsExtraServicesModalOpen(true)}
              onCancelConsolidation={handleCancelConsolidation}
            />
          </Suspense>
        )}

        {isAdmin && (activeTab === 'admin-finances' || activeTab === 'admin-analytics') && (
          <Suspense fallback={<LoadingFallback message="Carregando Análise Financeira..." />}>
            <AdminFinancialAnalytics
              shipments={shipments}
              personalShopperOrders={personalShopperOrders}
              packages={packages}
              yahooBids={yahooBids}
              allUsers={allUsers}
              onNavigateToTab={(tab) => setActiveTab(tab)}
              onOpenExchangeRateModal={() => setIsExchangeRateModalOpen(true)}
              onUpdateShipmentStatus={handleUpdateShipmentStatus}
              onUpdatePersonalShopperStatus={handleUpdatePersonalShopperStatus}
              onUpdateYahooBidStatus={handleUpdateYahooBidStatus}
              onUpdatePaymentDetails={handlePaymentComplete}
            />
          </Suspense>
        )}

        {isAdmin && activeTab === 'admin-clients' && (
          <Suspense fallback={<LoadingFallback message="Carregando Clientes..." />}>
            <AdminClientManagement
              allUsers={allUsers}
              packages={packages}
              shipments={shipments}
              onFilterPackagesBySuite={(suite) => {
                setSearchTerm(suite);
                setStatusFilter('ALL');
                setActiveTab('admin-packages');
              }}
            />
          </Suspense>
        )}

        {isAdmin && activeTab === 'admin-email-history' && (
          <Suspense fallback={<LoadingFallback message="Carregando Histórico de E-mails..." />}>
            <AdminEmailHistory
              onOpenSmtpConfigModal={() => setIsSmtpConfigOpen(true)}
            />
          </Suspense>
        )}

      </main>
      )}
      </Suspense>

      {/* Floating Bottom Bar for Consolidation Checkout (Client mode when packages selected) */}
      {!isAdmin && selectedPackageIds.length > 0 && (() => {
        const isAllSelectedFreeShipping = selectedPackagesList.length > 0 && selectedPackagesList.every(isPackageFreeShipping);
        return (
          <div className="sticky bottom-4 z-30 max-w-2xl mx-auto px-4 w-full animate-bounce-short">
            <div className={`p-4 rounded-2xl shadow-2xl border flex flex-col sm:flex-row items-center justify-between gap-3 backdrop-blur-md transition-all ${
              isAllSelectedFreeShipping
                ? 'bg-gradient-to-r from-emerald-950 via-teal-900 to-slate-900 border-emerald-500/50 text-white'
                : 'bg-[#1D3557] text-white border-slate-700'
            }`}>
              <div>
                <div className="flex items-center gap-2">
                  <span className={`px-2.5 py-0.5 font-black text-xs rounded-md ${
                    isAllSelectedFreeShipping ? 'bg-emerald-500 text-white shadow-xs' : 'bg-[#E63946] text-white'
                  }`}>
                    {selectedPackageIds.length} {selectedPackageIds.length === 1 ? 'Pacote' : 'Pacotes'}
                  </span>
                  <span className="font-bold text-xs text-slate-200">
                    {isAllSelectedFreeShipping ? '100% FRETE GRÁTIS SELECIONADO' : 'selecionados para consolidar'}
                  </span>
                </div>
                <div className="text-xs font-semibold text-slate-300 mt-1">
                  {isAllSelectedFreeShipping ? (
                    <div className="text-emerald-300 font-extrabold flex items-center gap-1.5 mt-0.5">
                      <span>Valor do Frete Internacional:</span>
                      <strong className="text-white bg-emerald-600 px-2.5 py-0.5 rounded-md font-mono text-xs uppercase shadow-2xs">
                        FRETE GRÁTIS (R$ 0,00)
                      </strong>
                    </div>
                  ) : (
                    <span>Peso Total Estimado: <strong className="text-white font-mono">{selectedTotalWeightKg.toFixed(2)} kg ({ (selectedTotalWeightKg * 2.20462).toFixed(2) } lb)</strong></span>
                  )}
                </div>
              </div>

              <button
                onClick={() => setIsCheckoutOpen(true)}
                className={`w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 font-extrabold text-xs sm:text-sm rounded-xl shadow-lg transition-all active:scale-95 text-white ${
                  isAllSelectedFreeShipping
                    ? 'bg-emerald-500 hover:bg-emerald-600 shadow-emerald-500/30'
                    : 'bg-[#E63946] hover:bg-[#d62839] shadow-red-500/20'
                }`}
              >
                <Plane className="w-4 h-4 text-white" />
                <span>Solicitar Consolidação</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        );
      })()}

      {/* Bento Styled Footer */}
      <footer className="bg-white border-t border-slate-200/80 py-8 mt-12 text-slate-500 text-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <Logo size="sm" showSubtitle={true} />
            <span className="text-slate-400 pl-3 border-l border-slate-200">
              © {new Date().getFullYear()} Do Japão by Egami Corporation | All Rights Reserved | Powered by Egami Corporation
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-3 font-bold text-slate-600">
            <button
              onClick={() => {
                if (isAdmin) {
                  setIsExchangeRateModalOpen(true);
                }
              }}
              title={isAdmin ? "Clique para configurar a cotação" : undefined}
              className={`px-2.5 py-1 bg-blue-50 text-blue-700 rounded-lg border border-blue-100 font-mono ${isAdmin ? 'cursor-pointer hover:bg-blue-100 transition-colors' : ''}`}
            >
              1 JPY = R$ {footerJpyRate.toFixed(4)}
            </button>
            <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-lg border border-emerald-100 font-mono">WMS v2.4 Active</span>
          </div>
        </div>
      </footer>

      {/* MODALS */}
      <Suspense fallback={null}>
        <NewTrackingModal
          isOpen={isNewTrackingOpen}
          onClose={() => setIsNewTrackingOpen(false)}
          onSubmit={handleCreateTracking}
          suiteNumber={currentUser.suiteNumber}
          userId={currentUser.uid}
          userName={currentUser.name}
          userEmail={currentUser.email}
        />

        <CheckoutConsolidationModal
          isOpen={isCheckoutOpen}
          onClose={() => setIsCheckoutOpen(false)}
          selectedPackages={selectedPackagesList}
          userAddress={currentUser.address}
          suiteNumber={currentUser.suiteNumber}
          userId={currentUser.uid}
          userName={currentUser.name}
          userEmail={currentUser.email}
          onSubmitConsolidation={handleSubmitConsolidation}
          isAdmin={isAdmin}
          currentUser={currentUser}
          shipments={clientShipments}
        />

        <AdminPackageEntryModal
          isOpen={isAdminEntryOpen}
          onClose={() => {
            setIsAdminEntryOpen(false);
            setEditingPackage(null);
          }}
          onSubmitPackageEntry={handleAdminPackageEntry}
          editingPackage={editingPackage}
          clientUsers={allUsers}
        />

        <AdminPackageExcelImportModal
          isOpen={isAdminExcelImportOpen}
          onClose={() => setIsAdminExcelImportOpen(false)}
          onConfirmImport={handleAdminPackageExcelImport}
          clientUsers={allUsers}
        />

        {paymentShipment && (
          <SimulatedPaymentModal
            isOpen={!!paymentShipment}
            onClose={() => setPaymentShipment(null)}
            shipment={paymentShipment}
            onPaymentComplete={handlePaymentComplete}
          />
        )}

        <Suspense fallback={null}>
          <AdminExtraServicesModal
            isOpen={isExtraServicesModalOpen}
            onClose={() => setIsExtraServicesModalOpen(false)}
          />
        </Suspense>

        {paymentPsOrder && (
          <PersonalShopperPaymentModal
            isOpen={!!paymentPsOrder}
            onClose={() => setPaymentPsOrder(null)}
            order={paymentPsOrder}
            onPaymentComplete={handlePaymentComplete}
          />
        )}

        
        {paymentCharge && (
          <CustomChargePaymentModal
            isOpen={!!paymentCharge}
            onClose={() => setPaymentCharge(null)}
            charge={paymentCharge}
            onPaymentComplete={async (chargeId, details) => {
              try {
                await updateDoc(doc(db, 'custom_charges', chargeId), {
                  status: 'Pago',
                  paymentDetails: details
                });
              } catch (e) {
                console.error(e);
                throw e;
              }
            }}
          />
        )}
  
        <AuthModal
          isOpen={isAuthOpen}
          onClose={() => setIsAuthOpen(false)}
          onLoginSuccess={(u) => {
            const isMasterAdmin = u.email?.toLowerCase() === MASTER_ADMIN_USER.email.toLowerCase() || u.uid === MASTER_ADMIN_USER.uid || u.role === 'admin';
            const finalUser: UserProfile = isMasterAdmin ? { ...u, ...MASTER_ADMIN_USER, role: 'admin', suiteNumber: 'DJ-ADMIN' } : u;
            setCurrentUser(finalUser);
            setIsLoggedIn(true);
            setActiveTab(finalUser.role === 'admin' ? 'admin-packages' : 'dashboard');
          }}
          demoUsers={allUsers}
          onUsersUpdate={(updated) => setAllUsers(updated)}
        />

        <LogoUploadModal
          isOpen={isLogoModalOpen}
          onClose={() => setIsLogoModalOpen(false)}
          customLogoUrl={customLogoUrl}
          customFaviconUrl={customFaviconUrl}
          siteTitle={siteTitle}
          onSaveBranding={handleSaveBranding}
          onSaveLogo={handleSaveLogo}
        />

        <ClientImportModal
          isOpen={isClientImportOpen}
          onClose={() => setIsClientImportOpen(false)}
          existingUsers={allUsers}
          onImportComplete={(imported) => {
            setAllUsers((prev) => {
              const userMap = new Map<string, UserProfile>();
              prev.forEach((u) => {
                if (u.uid) userMap.set(u.uid, u);
              });
              imported.forEach((u) => {
                if (u.uid) userMap.set(u.uid, u);
              });
              const merged = Array.from(userMap.values());
              try {
                localStorage.setItem('dojapao_all_users_v2', JSON.stringify(merged));
              } catch (e) {}
              return merged;
            });
          }}
        />

        <UserProfileModal
          isOpen={isUserProfileOpen}
          onClose={() => setIsUserProfileOpen(false)}
          currentUser={currentUser}
          onUpdateProfile={(updatedUser) => {
            setCurrentUser(updatedUser);
            setAllUsers((prev) => prev.map((u) => u.uid === updatedUser.uid ? updatedUser : u));
          }}
        />

        <GoogleDriveModal
          isOpen={isGoogleDriveOpen}
          onClose={() => setIsGoogleDriveOpen(false)}
          currentUser={currentUser}
          packages={packages}
          shipments={shipments}
        />

        <WhatsAppConfigModal
          isOpen={isWhatsAppConfigOpen}
          onClose={() => setIsWhatsAppConfigOpen(false)}
          adminPhone={currentUser.phone || '080-4859-1502'}
        />

        <PaymentMethodsModal
          isOpen={isPaymentMethodsModalOpen}
          onClose={() => setIsPaymentMethodsModalOpen(false)}
          isAdmin={currentUser.role === 'admin'}
        />

        <StorageFeePaymentModal
          isOpen={isStorageFeeModalOpen}
          onClose={() => setIsStorageFeeModalOpen(false)}
          pkg={storageFeeModalPkg}
          overdueMonths={storageFeeOverdueMonths}
          totalFeeUsd={storageFeeTotalUsd}
          onPaymentComplete={handleStorageFeePaymentComplete}
        />

        {/* Admin Home Popup Configurator */}
        <HomePopupConfigModal
          isOpen={isHomePopupConfigModalOpen}
          onClose={() => setIsHomePopupConfigModalOpen(false)}
          config={homePopupConfig}
          onSaveConfig={handleSaveHomePopupConfig}
          onTestDisplay={() => {
            setIsUserHomePopupVisible(true);
          }}
        />

        {/* User Home Popup Display Modal */}
        <HomePopupDisplayModal
          isOpen={isUserHomePopupVisible}
          onClose={() => {
            setIsUserHomePopupVisible(false);
            if (typeof window !== 'undefined') {
              sessionStorage.setItem('home_popup_closed', 'true');
            }
          }}
          config={homePopupConfig}
          onNavigateTab={(tab) => {
            setActiveTab(tab);
          }}
        />

        {/* InfinitePay Success Return Modal */}
        <InfinitePaySuccessModal
          isOpen={infinitePaySuccessData.isOpen}
          onClose={() => setInfinitePaySuccessData(prev => ({ ...prev, isOpen: false }))}
          orderNsu={infinitePaySuccessData.orderNsu}
          transactionNsu={infinitePaySuccessData.transactionNsu}
          captureMethod={infinitePaySuccessData.captureMethod}
          receiptUrl={infinitePaySuccessData.receiptUrl}
          slug={infinitePaySuccessData.slug}
        />

        {/* Admin Email Marketing & Newsletter Modal */}
        <AdminEmailMarketingModal
          isOpen={isEmailMarketingOpen}
          onClose={() => setIsEmailMarketingOpen(false)}
          allUsers={allUsers}
          currentUser={currentUser}
        />

        {/* Admin Website Email (SMTP) Config Modal */}
        <AdminSmtpConfigModal
          isOpen={isSmtpConfigOpen}
          onClose={() => setIsSmtpConfigOpen(false)}
          adminEmail={currentUser.email || 'egami.corporation@gmail.com'}
        />

        {/* Google Search Console & SEO Modal */}
        <GoogleSearchConsoleModal
          isOpen={isSearchConsoleOpen}
          onClose={() => setIsSearchConsoleOpen(false)}
        />

        {/* Admin Coupons Management Modal */}
        <AdminCouponsModal
          isOpen={isCouponsModalOpen}
          onClose={() => setIsCouponsModalOpen(false)}
        />

        {/* Admin Excel Products Import Modal */}
        <AdminExcelProductImportModal
          isOpen={isExcelProductImportModalOpen}
          onClose={() => setIsExcelProductImportModalOpen(false)}
        />

        {/* Admin Onamae.com MySQL Database & Storage Switch Modal */}
        <AdminOnamaeDbModal
          isOpen={isOnamaeDbModalOpen}
          onClose={() => setIsOnamaeDbModalOpen(false)}
          users={allUsers}
          packages={packages}
          shipments={shipments}
          personalShopperOrders={personalShopperOrders}
          yahooBids={yahooBids}
          tickets={tickets}
          notifications={notifications}
          blogPosts={blogPosts}
          homePopupConfig={homePopupConfig}
          onRestoreBackup={async (backup) => {
            if (backup.data.users) setAllUsers(backup.data.users);
            if (backup.data.packages) setPackages(backup.data.packages);
            if (backup.data.shipments) setShipments(backup.data.shipments);
            if (backup.data.personalShopperOrders) setPersonalShopperOrders(backup.data.personalShopperOrders);
            if (backup.data.yahooBids) setYahooBids(backup.data.yahooBids);
          }}
          onMasterReset={async ({ categories }) => {
            if (categories.includes('all') || categories.includes('clients')) {
              setAllUsers([MASTER_ADMIN_USER]);
            }
            if (categories.includes('all') || categories.includes('suiteProducts')) {
              setPackages([]);
            }
            if (categories.includes('all') || categories.includes('shipments')) {
              setShipments([]);
            }
            if (categories.includes('all') || categories.includes('salesFinancial')) {
              setPersonalShopperOrders([]);
              setYahooBids([]);
            }
          }}
        />

        {/* Admin Exchange Rate Configuration Modal */}
        <AdminExchangeRateModal
          isOpen={isExchangeRateModalOpen}
          onClose={() => setIsExchangeRateModalOpen(false)}
        />

        {/* Floating AI Customer Support Assistant (Egami) Trigger Button & Drawer */}
        {currentUser && (
          <>
            <AiSupportChat
              isOpen={isAiChatOpen}
              onClose={() => setIsAiChatOpen(false)}
              currentUser={currentUser}
              isLoggedIn={isLoggedIn}
            />

            {!isAiChatOpen && (
              <button
                onClick={() => setIsAiChatOpen(true)}
                className="fixed bottom-6 right-6 z-40 bg-gradient-to-r from-[#1D3557] to-slate-800 text-white p-3.5 sm:px-5 sm:py-3.5 rounded-full shadow-2xl hover:shadow-slate-800/30 border border-[#1D3557]/80 flex items-center gap-3 transition-all hover:scale-105 active:scale-95 group cursor-pointer"
                title="Atendente IA Egami - Tirar Dúvidas 24/7"
              >
                <div className="relative">
                  {localStorage.getItem('customBotIconUrl') ? (
                    <img
                      src={localStorage.getItem('customBotIconUrl')!}
                      alt="Egami Bot Icon"
                      className="w-8 h-8 rounded-full object-cover border border-amber-300 shadow-xs"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-amber-400 text-slate-900 flex items-center justify-center font-bold shadow-xs">
                      <Bot className="w-5 h-5 text-slate-900" />
                    </div>
                  )}
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full border-2 border-[#1D3557] animate-pulse" />
                </div>
                
                <div className="hidden sm:flex flex-col text-left">
                  <span className="text-xs font-bold text-white flex items-center gap-1">
                    Atendente IA Egami 🌸
                    <Sparkles className="w-3 h-3 text-amber-300" />
                  </span>
                  <span className="text-[10px] text-slate-300 font-medium">Tirar Dúvidas 24/7</span>
                </div>
              </button>
            )}
          </>
        )}
      </Suspense>

    </div>
  );
}
